<template>
  <TriadPullToRefresh
    :refreshing="isRefreshing"
    :disabled="editingSong || drawingEnabled || appStore.state.livePerformance"
    @refresh="handleRefresh"
  >
    <div class="relative containerSong bg-inherit text-gray-900 dark:text-white p-0 pt-0" :class="{ 'h-full flex flex-col overflow-hidden': editingSong }">
        <Transition name="fade" mode="out-in">
            <div class="flex fixed top-0 bg-black/25 left-0  w-full h-screen justify-center items-center z-[90]" 
                v-if="preparingToStart">
                <div class="font-bold" style="font-size: 100px;">{{ counterLivePerformance }}</div>
            </div>
        </Transition>
        <Transition name="slide-right" mode="out-in">
            <div v-if="appStore.state.livePerformance && !appStore.state.livePerformanceInControl"  
            class="flex flex-row gap-1 bg-slate-700 dark:bg-gray-800 rounded-md p-1 h-fit w-[125px] z-[150] justify-between items-center transition-all duration-300"
            :class="{'fixed right-10': appStore.state.livePerformance, 'top-2': !appStore.state.livePerformance}"
            :style="appStore.state.livePerformance ? { top: 'max(0.5rem, calc(env(safe-area-inset-top, 0px) + 0.85rem))' } : {}">
                <button @click="stopLivePerformance" class="size-6 text-gray-500 transition-all hover:text-gray-700 cursor-pointer">
                    <XMarkIcon class="size-6 text-gray-500 transition-all hover:text-gray-700 cursor-pointer" />
                </button>
                <TimeCounter ref="timeCounter" :disabled="!appStore.state.livePerformanceInControl" :startAutomatically="true" />
            </div>
        </Transition>
        <!-- Playlist Header-->
        <div class="flex flex-row items-center justify-start sticky -top-2 left-0 z-30 bg-[#ffffff] dark:bg-[#374151] transition-all duration-300 p-2 border-b border-gray-200 dark:border-gray-800 h-14 min-w-0 overflow-hidden">
            
            <div class="flex flex-row items-center justify-start flex-1 min-w-0 pl-12 md:pl-0">
                <div data-tour="song-list-toggle" class="flex-none size-8 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full flex-shrink-0">
                    <NumberedListIcon class="size-6 text-gray-500 transition-all hover:text-gray-700 dark:text-white dark:hover:text-slate-200 cursor-pointer" @click="openSongList" />
                </div>
                <div class="flex flex-1 flex-row items-center justify-start ml-1 md:ml-4 min-w-0 transition-all duration-300"
                     :style="isCurrentSongLocked ? 'filter: blur(8px); opacity: 0.5; pointer-events: none; user-select: none;' : ''">
                    <!-- Song Title-->
                    <div data-tour="song-header">
                        <ViewSongHeader :loading="showHeader" 
                            :title="songTitle" 
                            :artistName="songArtistName" 
                            :showFavorite="!editingSong"
                            :isFavorite="isSongFavorite"
                            @toggleFavorite="toggleSongFavorite"
                            @artistInfo="openArtistInfoModal" />
                    </div>
                    
                    <FavoriteMenuList 
                        data-tour="favorite-tools"
                        ref="favoriteBar" 
                        class="flex-1 ml-1 hidden sm:block" 
                        :floating="appStore.state.floatingMenu"
                        v-if="!editingSong"
                        :allowAccess="(isEditor() || isOwner()) && !editingSong && !musicNotationVisible"
                        @event="onFavoriteMenuEvent"/>
                    
                    <div v-show="!appStore.state.livePerformance && !preparingToStart" class="flex items-center gap-1 flex-shrink-0 ml-auto">
                        <!-- Realtime Connection Indicator Badge -->
                        <div
                            v-if="!editingSong"
                            class="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold border transition-all duration-200 flex-shrink-0 select-none cursor-pointer"
                            :class="realtimeStatusBadgeClass"
                            :title="realtimeStatusTooltip"
                            @click="showRealtimeDetails"
                        >
                            <span class="w-2 h-2 rounded-full" :class="realtimeStatusDotClass"></span>
                            <span class="hidden sm:inline text-[11px] font-medium">{{ realtimeStatusText }}</span>
                        </div>

                        <!-- Mobile trigger for Favorite Bottom Sheet -->
                        <button
                            data-tour="quick-actions-btn"
                            v-if="!editingSong && !musicNotationVisible"
                            type="button"
                            class="sm:hidden flex items-center justify-center p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex-shrink-0 shadow-sm active:scale-95 transform duration-150"
                            @click="openFavoriteBottomSheet"
                            title="Quick Actions"
                        >
                            <Squares2X2Icon class="size-5 text-indigo-500" />
                        </button>

                        <!-- Tour guide trigger button in header -->
                        <button
                            v-if="!editingSong"
                            type="button"
                            class="flex items-center justify-center p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-indigo-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex-shrink-0 shadow-sm active:scale-95 transform duration-150"
                            @click="() => startSongLiveTour(true)"
                            :title="$t('tour.startTour')"
                        >
                            <QuestionMarkCircleIcon class="size-5" />
                        </button>

                        <!-- Report Copyright / Objectionable Content button — only for public songs -->
                        <button
                            v-if="!editingSong && isSongPublic"
                            type="button"
                            class="flex items-center justify-center p-2 rounded-lg border border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/40 text-red-500 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/60 transition-colors flex-shrink-0 shadow-xs active:scale-95 transform duration-150 cursor-pointer"
                            @click="openReportModal"
                            :title="$t('report.buttonTooltip') || 'Denunciar Conteúdo / Direitos Autorais'"
                        >
                            <FlagIcon class="size-5" />
                        </button>
                    </div>
                </div>
            </div>
            
            <!-- Realtime Status-->
            <Transition name="fade" duration="250">
                <div v-if="!enabledRealtime && !appStore.state.disableEvents" class="absolute w-full h-1 bottom-0 left-0 border-b-2 border-red-400 animate-pulse animate-duration-1500"></div> 
            </Transition>
        </div>
        <div class="relative w-full flex flex-col justify-start" :class="editingSong ? 'flex-1 min-h-0 overflow-hidden' : 'min-h-[calc(100vh-56px)]'">
            <!-- Locked Song View Overlay (Center of screen) -->
            <div v-if="isCurrentSongLocked" class="flex flex-col items-center justify-center my-auto py-24 px-6 text-center max-w-md mx-auto z-[20]">
                <div class="w-16 h-16 rounded-full bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 flex items-center justify-center mb-5 shadow-sm">
                    <LockClosedIcon class="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h2 class="text-xl font-bold text-gray-900 dark:text-white tracking-wide uppercase">
                    {{ $t('lockedContent.title') || 'CONTEÚDO BLOQUEADO' }}
                </h2>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-2 leading-relaxed">
                    {{ $t('lockedContent.songViewDescription', { limit: 10 }) || 'Esta música está disponível apenas para assinantes. Usuários gratuitos têm acesso às primeiras 10 músicas da playlist.' }}
                </p>
                <button v-if="isSubscriptionEnabled()"
                        @click="openSubscribe" 
                        class="mt-6 px-8 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold uppercase tracking-wider shadow-md hover:shadow-lg transition-all active:scale-95 cursor-pointer flex items-center gap-2">
                    <span>{{ $t('lockedContent.subscribeButton') || 'Assine' }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-4 h-4">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                </button>
            </div>

            <!-- Loading State: skeleton card -->
            <div v-else-if="!song || (!viewerContent && !isCurrentSongLocked)" class="w-full h-full min-h-[calc(100vh-56px)] p-4 md:p-8 bg-[#ffffff] dark:bg-gray-700 flex flex-col justify-start">
                <CardLoading />
            </div>

            <div v-else-if="song" class="flex flex-cols main relative" :class="[{ 'hide-song-map': !songMapVisible }, editingSong ? 'flex-1 min-h-0 h-full overflow-hidden' : 'min-h-[calc(100vh-56px)]']">
            <div class="relative flex flex-col items-center w-full" :class="{ 'h-full min-h-0': editingSong }">
                <UserNotes ref="userNotesRef" :key="'notes-' + (storeKey || getSongId())" :storeKey="storeKey" :playlistId="route.params.id" :songId="getSongId()" />
                <Transition name="fade">
                    <SongCanvas v-if="!editingSong && !musicNotationVisible" 
                        :key="'canvas-' + getSongId()"
                        ref="drawingCanvas"
                        class="flex flex-col h-full w-full items-center song-canvas z-[30] pointer-events-none" 
                        targetSelector=".song-viewer"
                        :enabled="drawingEnabled"
                        :show-controls="false"
                        :room-id="getRoomId()"
                        :user-id="getUserId()"
                        :playlistId="route.params.id"
                        :songId="getSongId()"
                        :zoomValue="zoomValue"
                        :content-ready="songReady"
                        @addUserNotes="addUserNotes"
                    />
                </Transition>
                <DrawingToolbar 
                    v-if="drawingEnabled && !editingSong && !musicNotationVisible"
                    mode="circular"
                    :canvas-ref="drawingCanvas"
                    target-selector=".song-viewer"
                    :zoom-value="zoomValue"
                    @close="onDrawingEnabled(false)"
                />

                
                    <!-- Song Viewer -->
                    <div data-tour="song-viewer" class="w-full flex flex-col items-center" v-if="!musicNotationVisible && !editingSong"
                         :style="{ paddingBottom: 'max(6rem, calc(var(--sab, env(safe-area-inset-bottom, 0px)) + 5.5rem))' }">
                        <!-- Banner (inline, acima das cifras à esquerda — não sobrepõe nenhum elemento) -->
                        <div v-if="showCapoBanner"
                             data-capo-banner
                             class="relative w-full self-start mt-2 mb-4 flex items-start gap-3 max-w-3xl rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 pr-10 dark:bg-amber-900/20 dark:border-amber-700/40"
                             role="status">
                            <span class="flex flex-col items-center justify-center shrink-0 w-[54px] h-10 rounded-sm text-white text-[10px] font-extrabold uppercase leading-tight shadow-sm bg-gradient-to-br from-orange-500 to-amber-500">
                                <span>{{ capoBannerValue }}ª</span>
                                <span class="opacity-80">{{ $t('stage.capoBadgeLabel') }}</span>
                            </span>
                            <div class="flex-1 min-w-0 text-left">
                                <p class="flex flex-wrap items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-orange-900 dark:text-amber-100">
                                    <span>{{ $t('stage.capoInstruction') }}</span>
                                    <span class="inline-flex items-center rounded-md bg-amber-400/70 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-950 dark:bg-amber-500/40 dark:text-amber-50">
                                        {{ $t('stage.capoPill', { n: capoBannerValue }) }}
                                    </span>
                                </p>
                                <p class="mt-0.5 text-[11px] leading-snug text-orange-900/90 dark:text-amber-200/90">
                                    {{ $t('stage.capoDescription', { n: capoBannerValue }) }}
                                </p>
                            </div>
                            <button type="button"
                                    data-capo-banner-close
                                    @click.stop="closeCapoBanner"
                                    class="absolute top-2 right-2 inline-flex items-center justify-center p-1.5 rounded-full text-orange-500 hover:text-orange-700 hover:bg-orange-100/70 dark:text-amber-200 dark:hover:bg-amber-500/20 transition-colors"
                                    :title="$t('stage.capoHide')"
                                    :aria-label="$t('stage.capoHide')">
                                <XMarkIcon class="w-4 h-4" />
                            </button>
                        </div>
                        <div class="w-full flex justify-center">
                            <SongViewer ref="songComponent"
                                :key="'viewer-' + (song?.id || song?._id || '') + '-' + (viewerContent || '').length + '-' + songRenderKey"
                                :content="viewerContent"
                                :border="false"
                                :class="[columnView]"
                                :zoom="zoomValue"
                                @ready="onSongReady"
                            />
                        </div>
                    </div>
                    <!-- Song Editor -->
                    <SongEditor ref="songEditorComponent"
                        key="editor"
                        v-model="content"
                        :disabled="false"
                        mode="edit"
                        :border="false"
                        :class="['column-1', 'flex-1', 'min-h-0']"
                        :semitone="editingSemitone"
                        @transpose="onEditorTranspose"
                        @update:semitone="editingSemitone = $event"
                        v-else-if="!musicNotationVisible && editingSong"
                    />
                <Transition name="fade" mode="out-in">
                    <div id="music-notation" v-if="musicNotationVisible" 
                    class="w-full h-full max-h-full overflow-y-auto dark:bg-gray-900 z-[10]">
                        <MusicNotation ref="musicNotationComponent" :editable="isEditor() || isOwner()" 
                            @save="saveMusicNotation" 
                            :notation="musicNotation" />
                    </div>
                </Transition>
            
            </div>
        </div>
    </div>
        <!-- Player Song -->
        <PlayerSong 
            :url="selectedMedia ? selectedMedia.preview : ''"
            :type="selectedMedia ? selectedMedia.type : ''"
            :selectedMedia="selectedMedia"
            :metadata="song"
            :open="isOpenPlayer"
            :startTime="startPlaybackTime"
            @close="close"
            @next="nextMedia()"
            @previous="previousMedia()"
        />
        <Teleport to="body">
            <PlaylistMenuContainer 
                @updateFavorite="updateFavorite" 
                :allowAccess="(isEditor() || isOwner()) && !editingSong && !musicNotationVisible"
                v-if="!editingSong"/>
        </Teleport>
        <PlaylistMediaSidePanel 
            ref="mediaSidePanel"
            @play-in-main="playInMainPlayer"
        />
        <FavoriteBottomSheet
            ref="favoriteBottomSheet"
            :allowAccess="(isEditor() || isOwner()) && !editingSong && !musicNotationVisible"
            @event="onFavoriteMenuEvent"
        />
        <ModalShare ref="shareModal"/>
        <FloatingMenu :visible="editingSong" @save="saveSong(content)" @cancel="onCancelEditingSong()" />
        <SongListModal :isOpen="isSongListOpen" @close="closeSongList" :showEditor="isEditor() || isOwner()" />
        <StageSyncModal />
        <TriadDownloadOptionsModal 
            ref="downloadOptionsModal"
            :currentColumnLayout="columnView"
            :currentShowChords="chordsStore.state.visible"
            :currentShowNotes="showNotes"
            @download="performDownload"
        />
        <!-- Artist Info Modal using TriadModal -->
        <TriadModal ref="artistInfoModal" :title="$t('artistInfo.external.title')">
            <div class="max-h-96 overflow-y-auto">
                <MusicBrainzMetadata 
                    :artistName="song?.userData?.artist?.name || song?.data?.artist?.name" 
                    :showSection="true"
                    :autoSearch="true"
                />
            </div>
        </TriadModal>
        
        <!-- Realtime Controller & Live Performance Options Group (Bottom-Center) -->
        <Teleport to="body">
            <div v-if="(realtimeControllerInfo || (canControlRealtime && appStore.state.livePerformanceInControl)) && !editingSong && !isSideMenuOpen"
                v-show="!appStore.state.livePerformance"
                class="fixed left-1/2 transform -translate-x-1/2 z-40 flex items-center gap-1.5 sm:gap-2 select-none pointer-events-auto transition-all duration-300 ease-in-out max-w-[calc(100vw-180px)]"
                :style="centerGroupBottomStyle">
                
                <!-- Realtime Controller Badge -->
                <div v-if="realtimeControllerInfo"
                    @click="openRealtimeControl"
                    class="flex items-center gap-1.5 px-2.5 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-semibold select-none border bg-white/95 dark:bg-gray-800/95 backdrop-blur-md shadow-md transition-all duration-200 min-w-0"
                    :class="[
                        realtimeControllerInfo.isCurrentUser
                            ? 'border-indigo-200 text-indigo-700 dark:border-indigo-900/50 dark:text-indigo-300'
                            : 'border-gray-200 text-gray-700 dark:border-gray-700 dark:text-gray-300',
                        canManageRealtimeControl ? 'cursor-pointer hover:shadow-lg hover:border-indigo-300 active:scale-95' : ''
                    ]"
                    :title="canManageRealtimeControl ? `Realtime Controller: ${realtimeControllerInfo.name} — click to manage` : `Realtime Controller: ${realtimeControllerInfo.name}`"
                >
                    <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" 
                        :class="realtimeControllerInfo.isActive ? 'bg-green-500 animate-pulse' : 'bg-red-400'"
                    ></div>
                    <span class="flex-shrink-0">Control:</span>
                    <span class="max-w-[65px] xs:max-w-[100px] sm:max-w-[180px] truncate">{{ realtimeControllerInfo.name }}</span>
                </div>

                <!-- Live Performance Options Trigger (Inline alongside Controller Badge) -->
                <PlaylistOptions 
                    v-if="canControlRealtime && appStore.state.livePerformanceInControl"
                    :inline="true"
                    :playlistId="route.params.id || currentId || ''"
                    :startCounter="preparingToStart" 
                    @livePerformance="startLivePerformance" 
                />
            </div>

            <!-- Auto-Sync on Reconnect Floating Badge -->
            <Transition name="fade">
                <div v-if="reconnectingStatus !== 'idle'" 
                    class="fixed top-3 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold shadow-lg backdrop-blur-md transition-all duration-300 pointer-events-none"
                    :class="reconnectingStatus === 'syncing' 
                        ? 'bg-indigo-600/95 text-white border border-indigo-400/50 shadow-indigo-500/20' 
                        : 'bg-emerald-600/95 text-white border border-emerald-400/50 shadow-emerald-500/20'"
                >
                    <ArrowPathIcon v-if="reconnectingStatus === 'syncing'" class="size-4 animate-spin text-white flex-shrink-0" />
                    <CheckCircleIcon v-else class="size-4 text-white flex-shrink-0" />
                    <span>{{ reconnectingStatus === 'syncing' ? $t('playlistSong.syncingSong') : $t('playlistSong.songSynced') }}</span>
                </div>
            </Transition>

            <!-- Focus Mode Button (Bottom-Left: only on mobile devices where MobileBottomNav exists) -->
            <div v-if="!editingSong && !isSideMenuOpen && isSmallScreen"
                class="fixed left-3 sm:left-4 z-40 select-none transition-all duration-300 ease-in-out pointer-events-auto md:hidden"
                :style="centerGroupBottomStyle">
                <Tooltip :text="focusMode ? ($t('playlistSong.exitFocusMode') || 'Sair do Modo Foco') : ($t('playlistSong.enterFocusMode') || 'Modo Foco')" position="top">
                    <button 
                        type="button"
                        @click="toggleFocusMode"
                        class="size-9 sm:size-10 md:size-11 rounded-full p-1.5 sm:p-2 md:p-3 shadow-lg border transition-all duration-200 cursor-pointer flex items-center justify-center active:scale-95"
                        :class="focusMode 
                            ? 'bg-indigo-600 text-white border-indigo-700 shadow-indigo-500/30 ring-2 ring-indigo-400/50 scale-105' 
                            : 'bg-white/95 dark:bg-gray-800/95 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 backdrop-blur-md hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-400'"
                        :aria-label="focusMode ? 'Exit Focus Mode' : 'Enter Focus Mode'"
                    >
                        <component 
                            :is="focusMode ? ViewfinderCircleSolidIcon : ViewfinderCircleIcon" 
                            class="size-5 md:size-6 transition-transform duration-200" 
                        />
                    </button>
                </Tooltip>
            </div>
        </Teleport>
        <!-- Chord Diagrams Panel -->
        <ChordDiagramsPanel
            :visible="chordDiagramsVisible"
            :chords="uniqueSongChords"
            :highlightedChord="highlightedChord"
            @close="toggleChordDiagramsPanel(false)"
        />
        
        <!-- Download Progress Modal -->
        <div v-if="isDownloading" class="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50 transition-opacity">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 flex flex-col items-center max-w-sm w-full mx-4">
                <TriadLoad class="w-12 h-12 mb-4" />
                <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">Processing download...</h3>
                <p class="text-sm text-gray-500 dark:text-gray-400 text-center">Please wait while your PDF is being generated. This may take a moment.</p>
            </div>
        </div>

        <!-- Copyright / Content Infringement Report Modal -->
        <TriadCopyrightReportModal ref="reportModal" />
    </div>
  </TriadPullToRefresh>
</template>

<script setup>
/* eslint-disable */
import { onBeforeMount, onMounted, onUnmounted, ref, watch, nextTick, computed, reactive } from "vue";
import playlistStore from "@/store/playlist.store"
import appStore from "@/store/app.store"
import userStore from '@/store/user.store'
import searchModalStore from '@/store/search.modal.store'
import { useRoute, useRouter } from "vue-router";
import SongViewer from "@/components/editor/SongViewer.vue"
import SongEditor from "@/components/editor/SongEditor.vue"
import FloatingMenu from "@/components/FloatingMenu.vue"
import CardLoading from "@/components/TriadCardLoading.vue"
import ScrollControl from "@/components/ScrollControl.vue"
import ModalShare from "@/components/playlist/TriadModalShare.vue"
import songService from "@/service/song.service";
import { realtimeService } from '@/service/realtime.service'
import { transportManager } from '@/service/realtime/transport.manager'
import { nativeP2PService } from '@/service/realtime/native-p2p.provider'
import wakeLockService from '@/service/wakeLock.service'
import toastStore from '@/store/toast.store';
import dialogStore from '@/store/dialog.store';
import zoomStore from '@/store/zoom.store';
import autoscrollStore from '@/store/autoscroll.store';
import transposeStore from '@/store/transpose.store';
import shareModalStore from "@/store/share.modal.store";
import sideMenuStore from "@/store/sidemenu.store";
import { appEvents, openSubscriptionModal } from '@/store/app.events';
import { isSubscriptionEnabled } from '@/service/subscription.service';
import PlayerSong from "@/components/PlayerSong.vue";
import PlaylistOptions from "@/components/playlist/PlaylistOptions.vue";
import { NumberedListIcon, 
        EllipsisHorizontalCircleIcon, 
        XMarkIcon, MapIcon, DocumentDuplicateIcon,
        PencilIcon, Squares2X2Icon, PaperAirplaneIcon, LockClosedIcon,
        CheckCircleIcon, ArrowPathIcon,
        ViewfinderCircleIcon,
        QuestionMarkCircleIcon, FlagIcon } from '@heroicons/vue/24/outline';
import { useTour } from '@/composables/useTour';
import { MusicalNoteIcon, ViewfinderCircleIcon as ViewfinderCircleSolidIcon, MusicalNoteIcon as MusicalNoteSolidIcon } from '@heroicons/vue/24/solid';
import SongListModal from "@/components/playlist/SongListModal.vue";
import TriadDownloadOptionsModal from "@/components/song/TriadDownloadOptionsModal.vue";
import TriadCopyrightReportModal from "@/components/common/TriadCopyrightReportModal.vue";
import MusicBrainzMetadata from "@/components/artist/MusicBrainzMetadata.vue";
import TriadModal from "@/components/common/TriadModal.vue";
import TimeCounter from "@/components/common/TimeCounter.vue";
import MusicNotation from "@/components/song/MusicNotation.vue";
import { isMobile } from "@/service/utility";
import userConfigService from "@/service/userConfig.service";
import FavoriteMenuList from "@/components/common/menus/FavoriteMenuList.vue";
import PlaylistMenuContainer from "@/components/PlaylistMenuContainer.vue";
import ViewSongHeader from "@/components/song/ViewSongHeader.vue";
import chordsStore from "@/store/chords.store";
import PlaylistMediaSidePanel from "@/components/playlist/PlaylistMediaSidePanel.vue";
import FavoriteBottomSheet from "@/components/common/menus/FavoriteBottomSheet.vue";
import { transposeHtmlChords } from "@/utils/chordTransposer";
import { getInstrumentProfile } from "@/utils/instrumentTransposer";
import ChordDiagramsPanel from "@/components/song/ChordDiagramsPanel.vue";
import SongCanvas from "@/components/common/elements/SongCanvas.vue";
import DrawingToolbar from "@/components/common/elements/DrawingToolbar.vue";
import UserNotes from "@/components/common/elements/UserNotes.vue";
import drawingStore from '@/store/drawing.store';
import dbService from '@/service/db.service';
import playlistService from "@/service/playlist.service";
import TriadLoad from "@/components/TriadLoad.vue";
import StageSyncModal from "@/components/realtime/StageSyncModal.vue";
import tunerStore from "@/store/tuner.store";
import TriadPullToRefresh from "@/components/common/elements/TriadPullToRefresh.vue";

import { useI18n } from 'vue-i18n';
import { isNativePlatform, getPlatform, resolveAppUrl } from '@/utils/capacitor';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
const { t } = useI18n();
const route = useRoute()
const router = useRouter()
const isRefreshing = ref(false);
const getInitialSong = () => {
    const cur = playlistStore.state.currentSong;
    if (!cur) return null;
    const curPlaylist = playlistStore.state.playlist;
    const curPlaylistId = curPlaylist?.id || curPlaylist?._id;
    if (!curPlaylistId || String(curPlaylistId) !== String(route.params.id)) {
        return null;
    }
    if (route.params.songId) {
        const sSongId = cur.song_id || cur.songID || cur.song || cur.data?.id || cur.data?._id;
        const sId = cur.id || cur._id;
        if (sSongId && String(sSongId) === String(route.params.songId)) return cur;
        if (sId && String(sId) === String(route.params.songId)) return cur;
        return null;
    }
    return cur;
};
const song = ref(getInitialSong())
const songReady = ref(false)
const { startSongLiveTour } = useTour()
const onSongReady = () => {
    songReady.value = true
}
const connected = ref(false);
const isReactingToBroadcast = ref(false);
const currentId = ref(null)
const currentSongId = ref(null)
const editingSong = ref(false)
const editingSemitone = ref(0)
const focusMode = computed(() => appStore.state.focusMode)
const toggleFocusMode = () => {
    appStore.dispatch('toggleFocusMode');
};

const isSmallScreen = ref(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
const updateScreenSize = () => {
    isSmallScreen.value = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
    if (!isSmallScreen.value && appStore.state.focusMode) {
        appStore.commit('SET_FOCUS_MODE', false);
    }
};

const isSideMenuOpen = computed(() => {
    return (isSmallScreen.value || isMobile()) && sideMenuStore.state.open;
});

const centerGroupBottomStyle = computed(() => {
    if (!focusMode.value && isSmallScreen.value) {
        return {
            bottom: 'max(4.5rem, calc(var(--sab, env(safe-area-inset-bottom, 0px)) + 4.25rem))'
        };
    }
    return {
        bottom: 'max(1rem, calc(var(--sab, env(safe-area-inset-bottom, 0px)) + 1rem))'
    };
});
const userNotesRef = ref(null)
const showNotes = ref(false)
const content = ref('')
const songEditorComponent = ref(null)
const reconnectingStatus = ref('idle')
const songRenderKey = ref(0)

// Transpose realtime — fluxo autoritativo (backend é a fonte da verdade):
// editor aplica otimismo instantâneo -> persiste encadeado (um voo por vez, latest wins)
// -> Go salva + broadcast update-song -> receptores buscam a música autoritativa (fetch, sem matemática local).
// Sem isso, cada cliente derivava baseLyric de conteúdos diferentes e os tons divergiam/flapavam.
const transposePersistFlight = ref(null);
let transposePersistQueued = null;
const transposeAuthoritativeTimers = {};
// Tom pessoal (viewer sem controle): offset em semitons sobre o tom compartilhado,
// persistido por usuário/música. Reaplicado a cada adopt autoritativo.
// Reativo: viewerContent/viewerEffectiveChord (computed) precisam invalidar ao gravar o offset.
// Com objeto plano, o clique do viewer gravava mas a cifra em tela nunca atualizava.
const personalTransposeOffset = reactive({});
// Último tom compartilhado visto por música: detecta mudança vinda do editor para o viewer acompanhar.
const lastSharedChord = {};
const songIdentityKey = (s) => String(s?.id || s?._id || s?.data?.id || s?.data?._id || s?.songID || s?.song || '');
const loadPersonalOffset = (playlistId, songId) => {
    try {
        const off = Number(userConfigService.getCurrentUserSongConfigProperty(playlistId, songId, 'personalTransposeOffset', 0));
        return Number.isFinite(off) ? off : 0;
    } catch (e) { return 0; }
};
const persistPersonalOffset = (playlistId, songId, off) => {
    personalTransposeOffset[String(songId)] = off;
    try {
        userConfigService.setCurrentUserSongConfigProperty(playlistId, songId, 'personalTransposeOffset', off);
    } catch (e) { /* offline: mantém só em memória */ }
};
// Pino de tom do viewer: tom absoluto fixo que ignora mudanças do editor.
// sid -> number (fixado) ; ausente/null = seguindo o compartilhado.
const transposePinMap = reactive({});
const loadPinnedTone = (playlistId, songId) => {
    try {
        const raw = userConfigService.getCurrentUserSongConfigProperty(playlistId, songId, 'personalTransposePinned', null);
        if (raw === null || raw === undefined || raw === '') return null;
        const v = Number(raw);
        return Number.isFinite(v) ? v : null;
    } catch (e) { return null; }
};
const getPinnedTone = (sid, pid) => {
    if (!sid) return null;
    if (transposePinMap[sid] === undefined) {
        transposePinMap[sid] = loadPinnedTone(pid, sid);
    }
    return transposePinMap[sid] ?? null;
};
const setPinnedTone = (sid, pid, val) => {
    transposePinMap[String(sid)] = val;
    try {
        userConfigService.setCurrentUserSongConfigProperty(pid, sid, 'personalTransposePinned', val);
    } catch (e) { /* offline: mantém só em memória */ }
    updateTransposePinMirror();
};
// Espelho p/ o botão de pino no TriadTranspose (não conhece a música atual).
const updateTransposePinMirror = () => {
    const sid = String(songIdentityKey(song.value) || getVisibleSongId() || '');
    const pid = route.params.id || currentId.value;
    transposeStore.state.transposePin = {
        songId: sid,
        pinned: getPinnedTone(sid, pid),
        canPin: !canControlRealtime.value
    };
};
// Fixa/desfixa o tom atual do viewer. Fixado: mudanças do editor não alteram o local.
// Desfixar volta a acompanhar o compartilhado (offset 0).
const toggleTransposePin = () => {
    if (canControlRealtime.value || !song.value) return;
    const sid = String(songIdentityKey(song.value) || getVisibleSongId() || '');
    const pid = route.params.id || currentId.value;
    if (!sid) return;
    if (getPinnedTone(sid, pid) != null) {
        setPinnedTone(sid, pid, null);
        persistPersonalOffset(pid, sid, 0);
    } else {
        setPinnedTone(sid, pid, viewerEffectiveChord.value);
    }
    syncTransposeMenuDisplay();
};
const currentPersonalOffset = () => {
    if (canControlRealtime.value || !song.value) return 0;
    const sid = songIdentityKey(song.value) || getVisibleSongId();
    if (!sid) return 0;
    if (personalTransposeOffset[sid] === undefined) {
        personalTransposeOffset[sid] = loadPersonalOffset(route.params.id || currentId.value, sid);
    }
    return personalTransposeOffset[sid] || 0;
};
// Conteúdo exibido no SongViewer: compartilhado para o controller, compartilhado+offset
// pessoal para o viewer, somado ao offset de instrumento transpositor (Bb/Eb/F) e capotraste.
// O store/song guardam sempre o compartilhado (fonte da verdade).
const currentInstrumentCapoShift = computed(() => {
    const inst = getInstrumentProfile(transposeStore.state.instrument || 'concert_c');
    const capo = Number(transposeStore.state.capo || 0);
    return (inst?.offset || 0) - (Number.isFinite(capo) ? capo : 0);
});
const viewerContent = computed(() => {
    const shared = song.value?.modifiedLyric || song.value?.data?.content || song.value?.data?.lyric || '';
    const off = currentPersonalOffset() + currentInstrumentCapoShift.value;
    if (!off || !shared) return shared;
    return transposeHtmlChords(shared, off);
});
const viewerEffectiveChord = computed(() => {
    const shared = Number(song.value?.chord ?? transposeStore.state.transpose ?? 0);
    const off = currentPersonalOffset();
    return (Number.isFinite(shared) ? shared : 0) + (off || 0);
});

const loadInstrumentAndCapoPreference = () => {
    try {
        const playlistId = route.params.id || currentId.value;
        const songId = getVisibleSongId();
        const savedInst = userConfigService.getCurrentUserSongConfigProperty('global', 'defaults', 'instrument', 'concert_c');
        if (savedInst) {
            transposeStore.state.instrument = savedInst;
        }
        let savedCapo = 0;
        if (playlistId && songId) {
            savedCapo = Number(userConfigService.getCurrentUserSongConfigProperty(playlistId, songId, 'capo', 0)) || 0;
        }
        transposeStore.state.capo = savedCapo;
        // Estado de "fechar/esconder" do banner de capotraste (por música).
        savedCapoBannerDismissed.value = playlistId && songId
            ? (Number(userConfigService.getCurrentUserSongConfigProperty(playlistId, songId, 'capoBannerDismissed', 0)) || 0)
            : 0;

        // Restauração cross-device: o logout limpa o localStorage, então as prefs
        // duráveis do perfil (stage_preferences) precisam ser re-adotadas no load.
        if (playlistId && songId) {
            fetch(`/api/user/stage-preferences?playlistId=${encodeURIComponent(playlistId)}&songId=${encodeURIComponent(songId)}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((body) => {
                    const config = body?.data?.config;
                    if (!config) return;
                    if (config.instrument) {
                        transposeStore.state.instrument = config.instrument;
                        userConfigService.setCurrentUserSongConfigProperty('global', 'defaults', 'instrument', config.instrument);
                    }
                    if (config.capo !== undefined) {
                        const capoVal = Number(config.capo) || 0;
                        transposeStore.state.capo = capoVal;
                        userConfigService.setCurrentUserSongConfigProperty(playlistId, songId, 'capo', capoVal);
                    }
                    songRenderKey.value++;
                })
                .catch(() => {});
        }
    } catch (e) {
        console.error('Error loading instrument/capo preference:', e);
    }
};

// Estado do banner de capotraste: esconde a instrução por música (reaparece
// quando o capo muda de valor). O close persiste no config do usuário.
const savedCapoBannerDismissed = ref(0);
const capoBannerValue = computed(() => Number(transposeStore.state.capo || 0));
const showCapoBanner = computed(() => capoBannerValue.value > 0 && savedCapoBannerDismissed.value !== capoBannerValue.value);
// Reexibe o banner: limpa o estado "fechado" (persistido e refletido no ref).
const onShowCapoBanner = () => {
    savedCapoBannerDismissed.value = 0;
    const playlistId = route.params.id || currentId.value;
    const songId = getVisibleSongId();
    if (playlistId && songId) {
        try {
            userConfigService.setCurrentUserSongConfigProperty(playlistId, songId, 'capoBannerDismissed', 0);
        } catch (e) {
            console.warn('Error re-showing capo banner:', e);
        }
    }
};
const closeCapoBanner = () => {
    const val = capoBannerValue.value;
    savedCapoBannerDismissed.value = val;
    const playlistId = route.params.id || currentId.value;
    const songId = getVisibleSongId();
    if (playlistId && songId) {
        try {
            userConfigService.setCurrentUserSongConfigProperty(playlistId, songId, 'capoBannerDismissed', val);
        } catch (e) {
            console.warn('Error persisting capo banner dismissal:', e);
        }
    }
};

const onInstrumentTranspositionChanged = ({ instrument, capo } = {}) => {
    try {
        const playlistId = route.params.id || currentId.value;
        const songId = getVisibleSongId();
        if (instrument) {
            transposeStore.state.instrument = instrument;
            userConfigService.setCurrentUserSongConfigProperty('global', 'defaults', 'instrument', instrument);
        }
        if (capo !== undefined) {
            const c = Number(capo) || 0;
            transposeStore.state.capo = c;
            if (playlistId && songId) {
                userConfigService.setCurrentUserSongConfigProperty(playlistId, songId, 'capo', c);
            }
        }
        songRenderKey.value++;
        // Sync to backend stage preferences API asynchronously
        const prefPayload = {
            playlistId: playlistId || 'global',
            songId: songId || 'defaults',
            config: {
                instrument: transposeStore.state.instrument,
                instrumentOffset: getInstrumentProfile(transposeStore.state.instrument).offset,
                capo: transposeStore.state.capo
            }
        };
        const persistPref = () => fetch('/api/user/stage-preferences', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify(prefPayload)
        });
        persistPref()
            .then((res) => {
                if (!res || !res.ok) {
                    return new Promise((r) => setTimeout(r, 250)).then(() => persistPref());
                }
            })
            .catch(() => {
                setTimeout(() => persistPref().catch(() => {}), 250);
            });
    } catch (e) {
        console.error('Error saving instrument/capo preference:', e);
    }
};
// Marca atividade local de transpose por música: o editor ignora ecos do próprio persist
// (broadcast sem sender + fetch com leitura pré-commit) por 5s para não reverter o otimismo.
const localTransposeAt = {};
const LOCAL_TRANSPOSE_COOLDOWN_MS = 5000;
// Última época adotada por música (ordenação de respostas concorrentes).
const lastAdoptedFetchEpoch = {};
// Última edição local (save/transpose) por música: a verificação pós-reconnect não
// pode atropelar o que o usuário acabou de fazer.
const lastLocalSongEditAt = {};
const stampLocalSongEdit = (songId) => {
    const key = String(songId || getVisibleSongId() || '');
    if (key) lastLocalSongEditAt[key] = Date.now();
};

const getVisibleSongId = () => route.params.songId
    || song.value?.id || song.value?._id
    || song.value?.data?.id || song.value?.data?._id
    || song.value?.songID || song.value?.song
    || currentSongId.value || '';

const CHANNELS_STORAGE_KEY = 'notification_channels';

const saveChannelToStorage = (channelName, eventName = '*') => {
    try {
        const saved = JSON.parse(localStorage.getItem(CHANNELS_STORAGE_KEY) || '[]');
        const idx = saved.findIndex(s => s.channel === channelName);
        if (idx === -1) {
            saved.push({ channel: channelName, event: eventName });
            localStorage.setItem(CHANNELS_STORAGE_KEY, JSON.stringify(saved));
        }
    } catch (e) {
        console.error('Error saving channel to localStorage:', e);
    }
}

const removeChannelFromStorage = (channelName) => {
    try {
        const saved = JSON.parse(localStorage.getItem(CHANNELS_STORAGE_KEY) || '[]');
        const filtered = saved.filter(s => s.channel !== channelName);
        localStorage.setItem(CHANNELS_STORAGE_KEY, JSON.stringify(filtered));
    } catch (e) {
        console.error('Error removing channel from localStorage:', e);
    }
}
const enabledRealtime = computed(() => appStore.state.enabledRealtime);

const realtimeStatusBadgeClass = computed(() => {
    if (!enabledRealtime.value) {
        return 'border-rose-200 dark:border-rose-900/60 bg-rose-50/70 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300';
    }
    if (connected.value) {
        return 'border-emerald-200 dark:border-emerald-900/60 bg-emerald-50/70 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300';
    }
    return 'border-amber-200 dark:border-amber-900/60 bg-amber-50/70 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300';
});

const realtimeStatusDotClass = computed(() => {
    if (!enabledRealtime.value) {
        return 'bg-rose-500';
    }
    if (connected.value) {
        return 'bg-emerald-500 animate-pulse';
    }
    return 'bg-amber-500 animate-pulse';
});

const realtimeStatusText = computed(() => {
    if (!enabledRealtime.value) {
        return t('stage.syncOffline');
    }
    if (connected.value) {
        return t('stage.syncConnected');
    }
    return t('stage.syncConnecting');
});

const realtimeStatusTooltip = computed(() => {
    if (!enabledRealtime.value) {
        return t('stage.syncOfflineTooltip');
    }
    if (connected.value) {
        return t('stage.syncConnectedTooltip');
    }
    return t('stage.syncConnectingTooltip');
});

const showRealtimeDetails = () => {
    if (enabledRealtime.value && connected.value) {
        toastStore.dispatch('addMessage', {
            message: t('toast.sessionSynced'),
            icon: 'success',
            ttl: 2500
        });
    } else if (!enabledRealtime.value) {
        appStore.commit('SET_ENABLED_REALTIME', true);
        appEvents.emit('enabledRealtimeChanged', true);
        toastStore.dispatch('addMessage', {
            message: t('stage.syncConnectingTooltip'),
            icon: 'info',
            ttl: 2500
        });
    }
};
const scrollControlComponent = ref(null);
const shareModal = ref(null);
const selectedMedia = ref(null);
const medias = ref([]);
const isOpenPlayer = ref(false);
const currentMedia = ref(0);
const isSongListOpen = ref(false);
const isTransposing = ref(false);
const downloadOptionsModal = ref(null);
const artistInfoModal = ref(null);

const songMapVisible = ref(true);
const songComponent = ref(null);
const drawingEnabled = ref(false);
const drawingCanvas = ref(null);
const timeCounter = ref(null);
const musicNotationVisible = ref(false);
const musicNotation = ref('');
const musicNotationComponent = ref(null);
const favoriteBar = ref(null);
const mediaSidePanel = ref(null);
const favoriteBottomSheet = ref(null);
const startPlaybackTime = ref(0);

// Chord Diagrams Panel state
const chordDiagramsVisible = ref(false);
const highlightedChord = ref(null);
const isDownloading = ref(false);

// Extract unique chords from song content HTML
// A single .chord-item span may contain multiple chord tokens (e.g. "C/E Dm A/C#")
// We split by whitespace and deduplicate, preserving original slash-chord notation.
const uniqueSongChords = computed(() => {
    const content = song.value?.data?.content || song.value?.data?.lyric || '';
    if (!content) return []
    const parser = new DOMParser()
    const doc = parser.parseFromString(content, 'text/html')
    const chordElements = doc.querySelectorAll('sup.chord-sup, .chord-item')
    const seen = new Set()
    chordElements.forEach(el => {
        const text = (el.getAttribute('data-chord') || el.textContent || '').trim()
        if (!text) return
        // Split by one or more spaces/tabs in case the span holds multiple chords
        text.split(/\s+/).forEach(token => {
            const t = token.trim()
            if (t && /^[A-G]/.test(t)) seen.add(t)
        })
    })
    return Array.from(seen)
})

const loadChordDiagramsPreference = () => {
    try {
        const playlistId = route.params.id || currentId.value;
        const songId = getVisibleSongId();
        let saved = null;
        if (playlistId && songId) {
            saved = userConfigService.getCurrentUserSongConfigProperty(playlistId, songId, 'chordDiagramsVisible', null);
        }
        if (saved === null) {
            saved = userConfigService.getCurrentUserSongConfigProperty('global', 'defaults', 'chordDiagramsVisible', null);
        }
        if (saved !== null) {
            chordDiagramsVisible.value = Boolean(saved);
            appEvents.emit('currentChordDiagramsVisibility', chordDiagramsVisible.value);
        }
    } catch (e) {
        console.error('Error loading chord diagrams preference:', e);
    }
};

const toggleChordDiagramsPanel = (value) => {
    if (typeof value === 'boolean') {
        chordDiagramsVisible.value = value;
    } else {
        chordDiagramsVisible.value = !chordDiagramsVisible.value;
    }
    appEvents.emit('currentChordDiagramsVisibility', chordDiagramsVisible.value);
    try {
        const playlistId = route.params.id || currentId.value;
        const songId = getVisibleSongId();
        if (playlistId && songId) {
            userConfigService.setCurrentUserSongConfigProperty(playlistId, songId, 'chordDiagramsVisible', chordDiagramsVisible.value);
        }
        userConfigService.setCurrentUserSongConfigProperty('global', 'defaults', 'chordDiagramsVisible', chordDiagramsVisible.value);
    } catch (e) {
        console.error('Error saving chord diagrams preference:', e);
    }
};

const onChordClicked = (chordText) => {
    highlightedChord.value = chordText;
    if (!chordDiagramsVisible.value) {
        toggleChordDiagramsPanel(true);
    }
};

const isFreeUser = computed(() => {
    return userStore.getters.isFree;
});

const isCurrentSongLocked = computed(() => {
    const isFree = isFreeUser.value;
    const songs = playlistStore.state.playlist?.songs;
    const targetId = route.params.songId || song.value?.data?._id || song.value?.data?.id || song.value?.songID || song.value?.song || song.value?.id || song.value?._id;
    
    if (!isFree) {
        console.log(`[PlaylistSongView isCurrentSongLocked] isFree=false, returning false`);
        return false;
    }
    if (!songs || songs.length === 0) {
        console.log(`[PlaylistSongView isCurrentSongLocked] no songs in playlist, returning false`);
        return false;
    }

    if (!targetId) {
        const locked = (playlistStore.state.currentSongPosition || 0) >= 10;
        console.log(`[PlaylistSongView isCurrentSongLocked] no targetId, currentSongPosition=${playlistStore.state.currentSongPosition}, locked=${locked}`);
        return locked;
    }
    const index = songs.findIndex(s => {
        const sId = s.data?._id || s.data?.id || s.songID || s.song || s.id || s._id;
        return sId === targetId;
    });
    const locked = index !== -1 ? (index >= 10) : ((playlistStore.state.currentSongPosition || 0) >= 10);
    console.log(`[PlaylistSongView isCurrentSongLocked] targetId=${targetId}, index=${index}, locked=${locked}, totalSongs=${songs.length}`);
    return locked;
});

const openSubscribe = () => {
    openSubscriptionModal();
};

const isSongFavorite = computed(() => {
    if (!song.value || !song.value.data) return false;
    const songId = song.value.data._id || song.value.data.id || song.value.data.song;
    return searchModalStore.state.favorites.some(f => (f.id || f._id) === songId);
});

const toggleSongFavorite = () => {
    if (!song.value || !song.value.data) return;
    const songId = song.value.data._id || song.value.data.id || song.value.data.song;
    searchModalStore.dispatch("toggleFavorite", {
        id: songId,
        _id: songId
    });
}

const openFavoriteBottomSheet = () => {
    favoriteBottomSheet.value?.open();
};

let currentStyle = {
    "true":"inline",
    "false": "none" 
}
let columnView = ref("column-1")

let currentColumnIndex = 0
const columnValues = ["column-1", "column-2", "column-3"]
const DEFAULT_ZOOM_VALUE = 1;
const zoomValue = ref(DEFAULT_ZOOM_VALUE);

// Other localStorage keys (not using userConfig service)
const VISIBLE_CHORDS_KEY = 'playlist-song-visible-chords'

const counterLivePerformance = ref(1);

const sendPlaylistToTeam = () => {
    if (!appStore.state.activeTeam) {
        toastStore.dispatch("addMessage", { message: t('toast.noActiveTeam'), icon: "warning", ttl: 3000 });
        return;
    }
    const playlistId = route.params.id;
    const songId = route.params.songId || song.value?.data?._id || song.value?.data?.song || song.value?.data?.id; 
    realtimeService.publish(`team::${appStore.state.activeTeam}`, 'open-playlist', {
        playlistId: playlistId,
        songId: songId,
        title: playlistStore.state.playlist?.title || 'Playlist',
        type: 'PLAYLIST'
    });
    toastStore.dispatch("addMessage", { message: t('toast.playlistSentToTeam', { teamName: appStore.state.activeTeamName }), icon: "success", ttl: 3000 });
}

const isOwner = () => {
    if (!playlistStore.state.playlist) return false;
    const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
    if (!currentUserId) return false;
    const ownerId = playlistStore.state.playlist.userId ||
           playlistStore.state.playlist.user_id ||
           playlistStore.state.playlist.user?._id ||
           playlistStore.state.playlist.user?.id;
    return ownerId ? String(ownerId) === String(currentUserId) : false;
}

const isEditor = () => {
    if (!playlistStore.state.playlist) return false;
    if (isOwner()) return true;
    const perms = playlistStore.state.playlist.permissions;
    return Array.isArray(perms) && (perms.includes('editor') || perms.includes('owner') || perms.includes('admin'));
}

const canControlRealtime = computed(() => {
    if (!playlistStore.state.playlist) return false;
    const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
    const currentUserEmail = userStore.state.userInfo?.email;
    if (!currentUserId) return false;

    const rawDesignated = playlistStore.state.playlist.realtime_user_id;
    const designatedId = rawDesignated !== undefined && rawDesignated !== null
        ? rawDesignated
        : playlistStore.state.playlist.realtimeUserId;
    const rtUser = playlistStore.state.playlist.realtime_user;
    if (designatedId != null && String(designatedId) !== '' && String(designatedId) !== 'null' && String(designatedId) !== 'undefined') {
        if (String(designatedId) === String(currentUserId)) return true;
        // Fallback: check realtime_user object id/email match (handles id format mismatch for cto.developer@gmail.com)
        if (rtUser) {
            const rtId = rtUser.id || rtUser._id;
            const rtEmail = rtUser.email || rtUser.username;
            if (rtId && String(rtId) === String(currentUserId)) return true;
            if (rtEmail && currentUserEmail && String(rtEmail).toLowerCase() === String(currentUserEmail).toLowerCase()) return true;
            if (rtEmail && String(rtEmail) === String(designatedId)) {
                // designatedId is email but currentUserId is UUID: check via rtUser email
                if (currentUserEmail && String(rtEmail).toLowerCase() === String(currentUserEmail).toLowerCase()) return true;
            }
        }
        return false;
    }
    return isOwner();
});

const isEffectiveController = computed(() => {
    return canControlRealtime.value || transportManager.getWebRTC().role === 'host' || nativeP2PService.role === 'host';
});

watch(canControlRealtime, (newVal) => {
    appEvents.emit('canControlRealtimeChanged', newVal);
    updateTransposePinMirror();
}, { immediate: true });

const realtimeControllerInfo = computed(() => {
    const playlist = playlistStore.state.playlist;
    if (!playlist) return null;

    const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
    const rawControllerId = playlist.realtime_user_id;
    const controllerId = rawControllerId !== undefined && rawControllerId !== null
        ? rawControllerId
        : playlist.realtimeUserId;
    const ownerId = playlist.user_id || playlist.userId || playlist.user?.id || playlist.user?._id;

    const activeControllerId = (controllerId != null && String(controllerId) !== '' && String(controllerId) !== 'null' && String(controllerId) !== 'undefined') ? controllerId : ownerId;
    
    const currentUserEmail = userStore.state.userInfo?.email;
    const rtUser = playlist.realtime_user;
    let isCurrentUser = !!currentUserId && (String(activeControllerId) === String(currentUserId));
    if (!isCurrentUser && rtUser) {
        const rtId = rtUser.id || rtUser._id;
        const rtEmail = rtUser.email || rtUser.username;
        if (rtId && String(rtId) === String(currentUserId)) isCurrentUser = true;
        else if (rtEmail && currentUserEmail && String(rtEmail).toLowerCase() === String(currentUserEmail).toLowerCase()) isCurrentUser = true;
        else if (rtEmail && String(rtEmail) === String(activeControllerId) && currentUserEmail && String(rtEmail).toLowerCase() === String(currentUserEmail).toLowerCase()) isCurrentUser = true;
    }

    let controllerName = 'Owner';
    let debugSource = 'fallback Owner';
    if (playlist.realtime_user) {
        controllerName = playlist.realtime_user.full_name || playlist.realtime_user.fullName || playlist.realtime_user.name || playlist.realtime_user.username || playlist.realtime_user.email || 'Editor';
        debugSource = 'realtime_user object';
    } else if (isCurrentUser) {
        controllerName = 'You';
        debugSource = 'isCurrentUser true';
    } else if (String(activeControllerId) === String(ownerId)) {
        controllerName = playlist.user?.name || playlist.user?.full_name || 'Owner';
        debugSource = 'active==owner';
    } else {
        // Fallback when realtime_user is null but we have a designated id: try to show something meaningful
        // This happens when Go sends realtime_user_id but realtime_user is null
        if (controllerId) {
            controllerName = `User ${String(controllerId).slice(0,8)}`;
            debugSource = 'controllerId fallback';
        }
    }
    // Debug log for cto.developer@gmail.com issue: show why badge stays Owner
    console.log(`[realtimeControllerInfo] raw=${rawControllerId} controllerId=${controllerId} active=${activeControllerId} owner=${ownerId} current=${currentUserId} isCurrent=${isCurrentUser} realtime_user=${JSON.stringify(playlist.realtime_user)} name=${controllerName} source=${debugSource}`);
    return {
        id: activeControllerId,
        name: controllerName,
        isCurrentUser,
        isActive: !!enabledRealtime.value
    };
});

const activePlaylistSong = computed(() => {
    const sId = route.params.songId || currentSongId.value;
    if (sId && playlistStore.state.playlist?.songs?.length) {
        const found = playlistStore.state.playlist.songs.find(s => {
            const sSongId = s.song_id || s.songID || s.song || s.data?.id || s.data?._id;
            const songId = s.id || s._id;
            return (sSongId && String(sSongId) === String(sId)) || (songId && String(songId) === String(sId));
        });
        if (found) return found;
    }
    return playlistStore.state.currentSong || song.value;
});

const songTitle = computed(() => {
    return song.value?.userData?.title || 
        song.value?.data?.title || 
        song.value?.title || 
        activePlaylistSong.value?.userData?.title || 
        activePlaylistSong.value?.data?.title || 
        activePlaylistSong.value?.title || 
        '';
});

const songArtistName = computed(() => {
    return song.value?.userData?.artist?.name || 
        song.value?.data?.artist?.name || 
        (typeof song.value?.artist === 'string' ? song.value?.artist : song.value?.artist?.name) || 
        activePlaylistSong.value?.userData?.artist?.name || 
        activePlaylistSong.value?.data?.artist?.name || 
        (typeof activePlaylistSong.value?.artist === 'string' ? activePlaylistSong.value?.artist : activePlaylistSong.value?.artist?.name) || 
        '';
});

const showHeader = computed(() => {
    return !!songTitle.value || 
        (isTransposing.value && !!song.value) || 
        (!isTransposing.value && !playlistStore.state.loading && !!song.value);
});

const registerEvents = () => {
    appEvents.on('download', download);
    appEvents.on('duplicate', duplicatePlaylist);
    appEvents.on('presentation', presentation);
    appEvents.on('currentChordsVisibility', currentChordsVisibility);
    appEvents.on('toggleColumns', onColumnChange);
    appEvents.on('toggleMusicNotation', toggleMusicNotation);
    appEvents.on('toggleChordDiagrams', toggleChordDiagramsPanel);
    appEvents.on('chordClicked', onChordClicked);

    appEvents.on('toggleNotes', toggleNotes);
    appEvents.on('zoomIn', zoomIn);
    appEvents.on('zoomOut', zoomOut);
    appEvents.on('play', playSong);
    appEvents.on('currentTranspose', onTranspose);
    appEvents.on('toggleTransposePin', toggleTransposePin);
    appEvents.on('instrumentTranspositionChanged', onInstrumentTranspositionChanged);
    appEvents.on('showCapoBanner', onShowCapoBanner);
    appEvents.on('sharePlaylist', openShareModal);
    appEvents.on('editSong', editSong);
    appEvents.on('toggleSongMap', toggleSongMap);
    appEvents.on('addUserNotes', addUserNotes);
    appEvents.on('playlistUpdated', onPlaylistUpdated);
    appEvents.on('toggleDrawing', onDrawingEnabled);
    appEvents.on('sendPlaylistToTeam', sendPlaylistToTeam);
    appEvents.on('requestCanControlRealtime', onRequestCanControlRealtime);
    appEvents.on('app:online', onAppOnline);
}
const unregisterEvents = () => {
    appEvents.off('download', download);
    appEvents.off('duplicate', duplicatePlaylist)
    appEvents.off('presentation', presentation)
    appEvents.off('currentChordsVisibility', currentChordsVisibility)
    appEvents.off('toggleColumns', onColumnChange);
    appEvents.off('toggleMusicNotation', toggleMusicNotation);
    appEvents.off('toggleChordDiagrams', toggleChordDiagramsPanel);
    appEvents.off('chordClicked', onChordClicked);

    appEvents.off('toggleNotes', toggleNotes);
    appEvents.off('zoomIn', zoomIn);
    appEvents.off('zoomOut', zoomOut);
    appEvents.off('play', playSong);
    appEvents.off('currentTranspose', onTranspose);
    appEvents.off('toggleTransposePin', toggleTransposePin);
    appEvents.off('instrumentTranspositionChanged', onInstrumentTranspositionChanged);
    appEvents.off('showCapoBanner', onShowCapoBanner);
    appEvents.off('sharePlaylist', openShareModal);
    appEvents.off('editSong', editSong);
    appEvents.off('toggleSongMap', toggleSongMap);
    appEvents.off('addUserNotes', addUserNotes);
    appEvents.off('playlistUpdated', onPlaylistUpdated);
    appEvents.off('toggleDrawing', onDrawingEnabled);
    appEvents.off('sendPlaylistToTeam', sendPlaylistToTeam);
    appEvents.off('requestCanControlRealtime', onRequestCanControlRealtime);
    appEvents.off('app:online', onAppOnline);
}

const onRequestCanControlRealtime = () => {
    appEvents.emit('canControlRealtimeChanged', canControlRealtime.value);
}

// Load column preference from localStorage using userConfig service
const loadColumnPreference = () => {
    
    try {
        const playlistId = route.params.id
        // Try route param first, then song object as fallback
        let songId = route.params.songId
        if (isMobile(['iPad'])){
            columnView.value = columnValues[0];
            currentColumnIndex = 0;
            userConfigService.getCurrentUserSongConfigProperty(
                playlistId, 
                songId, 
                'columnView', 
                'column-1'
            )
            return;
        }
        if (!songId && song.value) {
            songId = song.value.data?.song || song.value.data?._id || song.value._id
        }
        
        if (!playlistId || !songId) {
            // Default to column-1 if playlist or song ID not available
            columnView.value = "column-1"
            currentColumnIndex = 0
            return
        }
        
        const userId = userConfigService.getUserId()
        if (!userId) {
            columnView.value = "column-1"
            currentColumnIndex = 0
            return
        }
        
        const savedColumn = userConfigService.getCurrentUserSongConfigProperty(
            playlistId, 
            songId, 
            'columnView', 
            'column-1'
        )
        
        if (savedColumn && columnValues.includes(savedColumn)) {
            columnView.value = savedColumn
            currentColumnIndex = columnValues.indexOf(savedColumn)
        } else {
            // Default to column-1 if invalid value
            columnView.value = "column-1"
            currentColumnIndex = 0
        }
    } catch (error) {
        console.error('Error loading column preference:', error)
        // Fallback to default
        columnView.value = "column-1"
        currentColumnIndex = 0
    }
}

// Save column preference to localStorage using userConfig service
const saveColumnPreference = (columnValue) => {
    try {
        const playlistId = route.params.id
        // Try route param first, then song object as fallback
        let songId = route.params.songId
        if (!songId && song.value) {
            songId = song.value.data?.song || song.value.data?._id || song.value._id
        }
        
        if (!playlistId || !songId) {
            console.warn('Cannot save column preference: playlist or song ID not available')
            return
        }
        
        userConfigService.setCurrentUserSongConfigProperty(
            playlistId, 
            songId, 
            'columnView', 
            columnValue
        )
    } catch (error) {
        console.error('Error saving column preference:', error)
    }
}

// Load zoom value from localStorage using userConfig service
const loadZoomValue = () => {
    try {
        const playlistId = route.params.id
        // Try route param first, then song object as fallback
        let songId = route.params.songId
        if (!songId && song.value) {
            songId = song.value.data?.song || song.value.data?._id || song.value._id
        }
        
        if (!playlistId || !songId) {
            // Default to 18 if playlist or song ID not available
            zoomValue.value = DEFAULT_ZOOM_VALUE
            return
        }
        
        const userId = userConfigService.getUserId()
        if (!userId) {
            zoomValue.value = DEFAULT_ZOOM_VALUE
            return
        }
        
        const savedZoom = userConfigService.getCurrentUserSongConfigProperty(
            playlistId, 
            songId, 
            'zoomValue', 
            DEFAULT_ZOOM_VALUE
        )
        if (savedZoom !== null && savedZoom !== undefined) {
            const zoomNumber = typeof savedZoom === 'number' ? savedZoom : parseFloat(savedZoom)
            if (!isNaN(zoomNumber) && zoomNumber >= 0.5 && zoomNumber <= 2) {
                zoomValue.value = zoomNumber
            } else {
                zoomValue.value = DEFAULT_ZOOM_VALUE
            }
        } else {
            zoomValue.value = DEFAULT_ZOOM_VALUE
        }
        zoomStore.state.zoom = zoomValue.value;
        appEvents.emit('currentZoom', zoomStore.state.zoom);
    } catch (error) {
        console.error('Error loading zoom value:', error)
        zoomValue.value = DEFAULT_ZOOM_VALUE
    }
}

// Save zoom value to localStorage using userConfig service
const saveZoomValue = (zoom) => {
    try {
        const playlistId = route.params.id
        // Try route param first, then song object as fallback
        let songId = route.params.songId
        if (!songId && song.value) {
            songId = song.value.data?.song || song.value.data?._id || song.value._id
        }
        
        if (!playlistId || !songId) {
            console.warn('Cannot save zoom value: playlist or song ID not available')
            return
        }
        zoomStore.state.zoom = Number(zoom.toFixed(1));
        userConfigService.setCurrentUserSongConfigProperty(
            playlistId, 
            songId, 
            'zoomValue', 
            zoomStore.state.zoom
        )
    } catch (error) {
        console.error('Error saving zoom value:', error)
    }
}

// Load visible chords preference from localStorage
const loadVisibleChordsPreference = () => {
    try {
        const savedVisibleChords = localStorage.getItem(VISIBLE_CHORDS_KEY)
        if (savedVisibleChords !== null) {
            chordsStore.state.visible = savedVisibleChords === 'true'
        }
        appEvents.emit('currentChordsVisibility', chordsStore.state.visible)
    } catch (error) {
        console.error('Error loading visible chords preference:', error)
    }
}

// Save visible chords preference to localStorage
const saveVisibleChordsPreference = (visible) => {
    try {
        localStorage.setItem(VISIBLE_CHORDS_KEY, chordsStore.state.visible.toString())
    } catch (error) {
        console.error('Error saving visible chords preference:', error)
    }
}



let playlistRoom = null;
let songRoom=null;
let stageSyncRoom = null;
let isMounted = false;

onMounted(() => {
    searchModalStore.dispatch('fetchFavorites');
});

watch( () => song.value, (newValue, oldValue) => {
    if(newValue && newValue.data && (newValue.data.content || newValue.data.lyric)){
        if (oldValue && oldValue.data && (oldValue.data.content || oldValue.data.lyric)){
            if ((oldValue.data.content || oldValue.data.lyric) != (newValue.data.content || newValue.data.lyric)){
                console.log('lyric changed')
            }
        } 
    }
    updateFavoriteMenuProps();

}, {deep: true})

const updateDisableGesture = () => {
    if (editingSong.value || drawingEnabled.value) {
        appStore.state.disableGesture = true;
    } else {
        const savedDisableGesture = localStorage.getItem('disableGesture');
        appStore.state.disableGesture = savedDisableGesture === 'true';
    }
}

watch([editingSong, drawingEnabled], updateDisableGesture)

watch(drawingEnabled, (value) => {
    drawingStore.state.drawingEnabled = value;
})

const updateFavoriteMenuProps = () => {
    nextTick(() => {
        if (favoriteBar.value){
            const autoscrollMenu = favoriteBar.value.getComponentRef('autoscroll')
            if (autoscrollMenu){
                autoscrollMenu.stopScrolling();
            }
            if (song.value){
                const transposeMenu = favoriteBar.value.getComponentRef('transpose')
                // Menu exibe o tom efetivo (com offset pessoal para o viewer).
                transposeStore.state.transpose = viewerEffectiveChord.value;
                favoriteBar.value.setProps('transpose', {song: song.value})
                if (transposeMenu){
                    transposeMenu.updateSemitone(viewerEffectiveChord.value);
                    isTransposing.value = false;
                }
            }
        }
    });
}
const loadPage = async () => {
    const newId = route.params.id;
    const newSongId = route.params.songId;

    musicNotationVisible.value = false;

    const currentPlaylistId = playlistStore.state.playlist?.id || playlistStore.state.playlist?._id;
    const isSamePlaylist = newId === currentId.value && !!playlistStore.state.playlist && String(currentPlaylistId) === String(newId);

    if (!isSamePlaylist) {
        song.value = null;
        playlistStore.state.currentSong = null;
        playlistStore.state.playlist = null;
        songReady.value = false;
        playlistStore.commit("LOADING", true);
    } else if (newSongId && song.value) {
        const sSongId = song.value.song_id || song.value.songID || song.value.song || song.value.data?.id || song.value.data?._id;
        const sId = song.value.id || song.value._id;
        const isCurrentSongMatch = (sSongId && String(sSongId) === String(newSongId)) || (sId && String(sId) === String(newSongId));
        if (!isCurrentSongMatch) {
            // Optimistically update song.value with playlist metadata to avoid header flashing "Loading..."
            const nextSongItem = playlistStore.state.playlist?.songs?.find(s => {
                const itemSongId = s.song_id || s.songID || s.song || s.data?.id || s.data?._id;
                const itemId = s.id || s._id;
                return (itemSongId && String(itemSongId) === String(newSongId)) || (itemId && String(itemId) === String(newSongId));
            });
            if (nextSongItem) {
                song.value = nextSongItem;
            } else {
                song.value = null;
            }
            songReady.value = false;
            playlistStore.commit("LOADING", true);
        }
    }

    // Only detach and re-attach the playlist room if the playlist ID actually changed
    if (!isSamePlaylist && playlistRoom) {
        realtimeService.unsubscribe("playlist::room::"+currentId.value);
        removeChannelFromStorage("playlist::room::"+currentId.value);
        playlistRoom = null;
    }

    if (songRoom) {
        realtimeService.unsubscribe("playlist::room::"+currentId.value+"::"+currentSongId.value);
        removeChannelFromStorage("playlist::room::"+currentId.value+"::"+currentSongId.value);
        songRoom = null;
    }

    currentId.value = newId;
    currentSongId.value = newSongId;
    const wasReacting = isReactingToBroadcast.value;
    const canControl = canControlRealtime.value;
    // Frontend-only realtime: nunca usa realtimeStatus no backend para troca de música.
    // O broadcast é feito exclusivamente via Ably/P2P direto do frontend para evitar
    // duplicidade e o workaround cache:'no-store' + GET com efeito colateral.
    isReactingToBroadcast.value = false;

    const hasP2PStageControl = transportManager.getWebRTC().role === 'host' || nativeP2PService.role === 'host';
    const effectiveCanControl = canControl || canControlRealtime.value || hasP2PStageControl;

    // Dispara broadcast da troca de música IMEDIATAMENTE (0ms, sem aguardar resposta HTTP do loadSong)
    console.log(`[PlaylistSongView] change-song decision: songId=${newSongId} canControl=${canControl} canControlNow=${canControlRealtime.value} p2pHost=${hasP2PStageControl} wasReacting=${wasReacting} native=${isNativePlatform()} userId=${userStore.state.userInfo?.id || userStore.state.userInfo?._id} realtimeUser=${playlistStore.state.playlist?.realtime_user_id} ownerId=${playlistStore.state.playlist?.userId || playlistStore.state.playlist?.user_id || playlistStore.state.playlist?.user?.id}`);
    if (newSongId && effectiveCanControl && enabledRealtime.value && !wasReacting) {
        try {
            const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
            const payload = {
                _id: newSongId,
                id: newSongId,
                songId: newSongId,
                playlistId: newId,
                sender: currentUserId,
                sender_client_id: window.myClientId || null
            };
            console.log(`[PlaylistSongView] immediate frontend-only publish change-song:`, payload);
            realtimeService.publish(`playlist::room::${newId}`, 'change-song', payload).catch(e => console.warn('[PlaylistSongView] publish change-song failed', e));
        } catch (e) {
            console.warn('[PlaylistSongView] publish error', e);
        }
    }

    if (isSamePlaylist && newSongId) {
        await playlistStore.dispatch("loadSong", { id: newId, songId: newSongId, realtimeStatus: false });
    } else {
        await playlistStore.dispatch("load", {...route.params, realtimeStatus: false});
    }

    if(playlistStore.state.error) {
        appStore.state.error = playlistStore.state.status;
        appStore.state.message = playlistStore.state.message;
        router.push({path: "/error"});
    } else {
        if (!currentSongId.value && playlistStore.state.playlist && playlistStore.state.playlist.songs && playlistStore.state.playlist.songs.length > 0) {
            const firstSong = playlistStore.state.playlist.songs[0];
            const firstSongId = firstSong?.data?.id || firstSong?.data?._id || firstSong?.songID || firstSong?.song || firstSong?.id || firstSong?._id;
            if (firstSongId) {
                router.replace({path: `/playlist/${currentId.value}/song/${firstSongId}`});
                return;
            }
        }
        // After playlist loads successfully, load column and zoom preferences
        nextTick(() => {
            songReady.value = true;
            loadColumnPreference();
            loadZoomValue();
            loadInstrumentAndCapoPreference();
            autoscrollStore.state.isScrolling = false;
            autoscrollStore.state.scrollSpeed = 1;
        });
    }

    // Guard against stale async calls after component unmount
    if (!isMounted) return;

    // Skip re-subscribing to the playlist room if we're on the same playlist
    if (!isSamePlaylist || !playlistRoom) {
        playlistRoom = realtimeService.subscribe(
            "playlist::room::"+currentId.value,
            '*',
            (payload) => onEvent({event: payload.event, data: payload}),
            (status) => {
                if (status == 'SUBSCRIBED') { 
                    connected.value = true;
                    saveChannelToStorage("playlist::room::"+currentId.value);
                    if (!isEffectiveController.value && enabledRealtime.value && currentId.value) {
                        const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
                        realtimeService.publish(`playlist::room::${currentId.value}`, 'sync-request', {
                            sender: currentUserId,
                            sender_client_id: window.myClientId || null,
                            requestedAt: Date.now()
                        }).catch(() => {});
                    }
                } else {
                    connected.value = false;
                }
            }
        )
    }

    songRoom = realtimeService.subscribe(
        "playlist::room::"+currentId.value+"::"+currentSongId.value,
        '*',
        (payload) => onEvent({event: payload.event, data: payload}),
        (status) => {
            if (status == 'SUBSCRIBED') {
                saveChannelToStorage("playlist::room::"+currentId.value+"::"+currentSongId.value);
            }
        }
    )

    if (!stageSyncRoom) {
        stageSyncRoom = realtimeService.subscribe(
            "stage-sync",
            '*',
            (payload) => onEvent({event: payload.event, data: payload})
        );
    }
}

const onPlaylistUpdated = (playlistId) => {
    if (currentId.value === playlistId) {
        playlistStore.state.forceUpdate = true;
        playlistStore.dispatch("load", {...route.params, realtimeStatus: enabledRealtime.value});
    }
}

const flushPendingDesignation = async (pId) => {
    // Designação feita offline (sessão): persiste no banco na volta da internet.
    if (!pId) return;
    try {
        const raw = localStorage.getItem(`triad_pending_designation::${pId}`);
        if (!raw) return;
        const pending = JSON.parse(raw);
        if (!pending || pending.realtime_user_id === undefined) return;
        const res = await fetch(`/api/playlist/${pId}`, {
            method: 'PUT',
            body: JSON.stringify({ realtime_user_id: pending.realtime_user_id }),
            headers: { 'Content-Type': 'application/json' }
        });
        if (res.ok) {
            localStorage.removeItem(`triad_pending_designation::${pId}`);
            console.log('[PlaylistSongView] pending designation synced');
        }
    } catch (e) {}
};

// Timeout por etapa do resync: backend lento (pós-queda) não pode travar badge/skeleton.
const ONLINE_SYNC_TIMEOUT_MS = 15000;
const withOnlineTimeout = (promise, label) => Promise.race([
    Promise.resolve(promise),
    new Promise((_, reject) => setTimeout(() => reject(new Error(`[sync-timeout] ${label}`)), ONLINE_SYNC_TIMEOUT_MS))
]);
let onlineSyncWatchdog = null;

const onAppOnline = async () => {
    if (!isMounted) return;
    // Evita tempestade de resyncs concorrentes (window online + interceptor recovery emitem juntos).
    if (reconnectingStatus.value === 'syncing') {
        console.log('[PlaylistSongView] onAppOnline skipped (already syncing)');
        return;
    }
    reconnectingStatus.value = 'syncing';
    // Watchdog: badge nunca pode grudar em 'syncing'.
    if (onlineSyncWatchdog) clearTimeout(onlineSyncWatchdog);
    onlineSyncWatchdog = setTimeout(() => {
        if (reconnectingStatus.value === 'syncing') {
            console.warn('[PlaylistSongView] sync watchdog: forcing idle');
            reconnectingStatus.value = 'idle';
            playlistStore.commit('LOADING', false);
        }
    }, ONLINE_SYNC_TIMEOUT_MS + 10000);

    try {
        const pId = route.params.id || currentId.value;
        const sId = route.params.songId || currentSongId.value;

        try {
            await withOnlineTimeout(flushPendingDesignation(pId), 'designation');
        } catch (e) {
            console.warn('[PlaylistSongView] flushPendingDesignation failed/slow:', e?.message);
        }

        // Clear in-memory cached copies so fresh data from server is always retrieved
        if (pId && window.cacheInstance) {
            const playlistKey = playlistService.PLAYLIST_CACHE_KEY.replace("[id]", pId);
            window.cacheInstance.removeItem(playlistKey);
            if (sId) {
                const songKey = playlistService.PLAYLIST_SONG_CACHE_KEY.replace("[id]", pId).replace("[songId]", sId);
                window.cacheInstance.removeItem(songKey);
            }
        }

        playlistStore.state.forceUpdate = true;

        if (pId) {
            try {
                await withOnlineTimeout(
                    playlistStore.dispatch("load", { id: pId, songId: sId, force: true, realtimeStatus: enabledRealtime.value }),
                    'load'
                );
            } catch (e) {
                // Mantém o conteúdo em cache em tela; o badge sai no finally.
                console.warn('[PlaylistSongView] online reload (load) failed/slow:', e?.message);
            }
        }
        if (pId && sId) {
            try {
                await withOnlineTimeout(
                    playlistStore.dispatch("loadSong", { id: pId, songId: sId, force: true, realtimeStatus: enabledRealtime.value }),
                    'loadSong'
                );
            } catch (e) {
                console.warn('[PlaylistSongView] online reload (loadSong) failed/slow:', e?.message);
            }
        }

        songRenderKey.value++;

        nextTick(() => {
            loadVisibleChordsPreference();
            loadColumnPreference();
            changeChordsVisibility(chordsStore.state.visible);
        });

        // Re-subscribe realtime rooms to recover dropped websocket connections
        if (playlistRoom) {
            realtimeService.unsubscribe("playlist::room::" + currentId.value);
            playlistRoom = null;
        }
        if (songRoom) {
            realtimeService.unsubscribe("playlist::room::" + currentId.value + "::" + currentSongId.value);
            songRoom = null;
        }

        if (currentId.value) {
            playlistRoom = realtimeService.subscribe(
                "playlist::room::" + currentId.value,
                '*',
                (payload) => onEvent({ event: payload.event, data: payload }),
                (status) => {
                    if (status === 'SUBSCRIBED') {
                        connected.value = true;
                        saveChannelToStorage("playlist::room::" + currentId.value);
                    } else {
                        connected.value = false;
                    }
                }
            );
        }

        if (currentId.value && currentSongId.value) {
            songRoom = realtimeService.subscribe(
                "playlist::room::" + currentId.value + "::" + currentSongId.value,
                '*',
                (payload) => onEvent({ event: payload.event, data: payload }),
                (status) => {
                    if (status === 'SUBSCRIBED') {
                        saveChannelToStorage("playlist::room::" + currentId.value + "::" + currentSongId.value);
                    }
                }
            );
        }

        reconnectingStatus.value = 'synced';
        setTimeout(() => {
            if (reconnectingStatus.value === 'synced') {
                reconnectingStatus.value = 'idle';
            }
        }, 3000);
    } catch (err) {
        console.warn('[PlaylistSongView] Error syncing on reconnect:', err);
        reconnectingStatus.value = 'idle';
    } finally {
        if (onlineSyncWatchdog) {
            clearTimeout(onlineSyncWatchdog);
            onlineSyncWatchdog = null;
        }
        // Garante que nem badge nem skeleton grudem se algum fetch pendurar.
        if (reconnectingStatus.value === 'syncing') {
            reconnectingStatus.value = 'idle';
        }
        playlistStore.commit('LOADING', false);
    }

    // Verificação final (silenciosa): cobre inversões residuais (eco AAAA resolvendo após
    // o reload). Lê a verdade do backend; epoch descarta se for mais antiga que o adotado.
    // Pula se o usuário editou há pouco ou há transpose em voo.
    const verifySongId = route.params.songId || currentSongId.value || '';
    setTimeout(() => {
        if (!isMounted || !verifySongId || String(getVisibleSongId()) !== String(verifySongId)) return;
        if (Date.now() - (lastLocalSongEditAt[String(verifySongId)] || 0) < 8000) return;
        if (transposePersistFlight.value || transposePersistQueued) return;
        playlistStore.dispatch("loadSong", { id: route.params.id || currentId.value, songId: verifySongId, force: true }).catch(() => {});
    }, 2500);
};

const editSong = () => {
    content.value = song.value.modifiedLyric || song.value.data?.content || song.value.data?.lyric || ''
    editingSemitone.value = Number(song.value?.chord || 0);
    editingSong.value = true;
    appStore.state.disableEvents = true;
}

const onEditingSong = (event) => {
    content.value = song.value.modifiedLyric || song.value.data?.content || song.value.data?.lyric || ''
    editingSemitone.value = Number(song.value?.chord || 0);
    editingSong.value=event
    appStore.state.disableEvents = event;
}

const onCancelEditingSong = () => {
    editingSong.value=false
    appStore.state.disableEvents=false;
    playlistStore.state.forceUpdate=true
    playlistStore.dispatch("load", {...route.params, realtimeStatus: enabledRealtime.value})
}

const onEditorTranspose = async (event) => {
    if (!event) return;
    stampLocalSongEdit(route.params.songId);
    const semitone = Number(event.semitone || 0);
    const delta = Number(event.delta || 0);

    editingSemitone.value = semitone;
    if (song.value) song.value.chord = semitone;

    if (!content.value || delta === 0) {
        return;
    }

    // 1. Instant client-side transposition
    const localTransposed = transposeHtmlChords(content.value, delta);
    content.value = localTransposed;
    if (song.value) {
        song.value.modifiedLyric = localTransposed;
    }

    // 2. If online, optionally synchronize with backend tools
    if (navigator.onLine) {
        try {
            const response = await fetch('/api/song/transpose-html', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ html: content.value, semitone: 0 })
            });
            if (response.ok) {
                const data = await response.json();
                if (data.html) {
                    content.value = data.html;
                }
            }
        } catch (e) {
            console.warn('Backend onEditorTranspose error:', e);
        }
    }
};

const saveSong = async (_lyricHtml) => {
    let request = null
    const editorHtml = songEditorComponent.value?.editor?.getHTML?.();
    const rawHtmlContent = editorHtml || ((typeof _lyricHtml === 'string' && _lyricHtml) ? _lyricHtml : content.value);
    const currentChord = editingSemitone.value !== undefined ? editingSemitone.value : Number(song.value?.chord || 0);
    stampLocalSongEdit(route.params.songId);

    if (song.value) {
        song.value.modifiedLyric = rawHtmlContent;
        song.value.baseLyric = currentChord === 0 ? rawHtmlContent : transposeHtmlChords(rawHtmlContent, -currentChord);
        if (song.value.data) {
            song.value.data.content = rawHtmlContent;
            song.value.data.lyric = rawHtmlContent;
        }
    }
    content.value = rawHtmlContent;

    if (playlistStore.state.currentSong) {
        playlistStore.state.currentSong.modifiedLyric = rawHtmlContent;
        playlistStore.state.currentSong.baseLyric = song.value?.baseLyric || rawHtmlContent;
        if (playlistStore.state.currentSong.data) {
            playlistStore.state.currentSong.data.content = rawHtmlContent;
            playlistStore.state.currentSong.data.lyric = rawHtmlContent;
        }
    }
    if (playlistStore.state.playlist?.songs) {
        const songIdx = playlistStore.state.playlist.songs.findIndex(s => 
            (s.data?.id || s.data?._id) === route.params.songId || 
            (s.songID || s.song) === route.params.songId || 
            s.id === route.params.songId
        );
        if (songIdx > -1) {
            playlistStore.state.playlist.songs[songIdx].modifiedLyric = rawHtmlContent;
            if (playlistStore.state.playlist.songs[songIdx].data) {
                playlistStore.state.playlist.songs[songIdx].data.content = rawHtmlContent;
                playlistStore.state.playlist.songs[songIdx].data.lyric = rawHtmlContent;
            }
        }
    }
    const isSongType = song.value?.type === "SONG" || !!song.value?.data;
    const isTextType = song.value?.type === "TEXT" || (!song.value?.data && song.value?.text !== undefined);

    try {
        if (isSongType){
            const songVersionId = song.value.songVersionId || song.value.data?.songVersionId;
            
            // Preserve title, artist, media, and tags from existing song data so they are not wiped on save
            const songData = song.value?.data || song.value?.userData || song.value || {};
            const title = songData.title || song.value?.title || '';
            const artistObj = songData.artist || song.value?.artist || null;
            const artist = artistObj ? (artistObj._id || artistObj.id ? { id: artistObj._id || artistObj.id, name: artistObj.name } : { name: artistObj.name || artistObj }) : null;
            
            const mediaList = (medias.value && medias.value.length > 0)
                ? medias.value.map(m => typeof m === 'object' ? (m.preview || m.url || m) : m).filter(Boolean)
                : (songData.media || song.value?.media || []).map(m => typeof m === 'object' ? (m.preview || m.url || m) : m).filter(Boolean);
                
            // Check if there are custom tags in songMetadataChanged to preserve them
            const metadataChanged = song.value.songMetadataChanged || song.value.song_metadata_changed;
            let parsedMetadata = {};
            if (typeof metadataChanged === 'string') {
                try { parsedMetadata = JSON.parse(metadataChanged); } catch (e) {}
            } else if (metadataChanged) {
                parsedMetadata = metadataChanged;
            }
            
            const customTags = parsedMetadata.tags;
            let tagList = [];
            if (Array.isArray(customTags) && customTags.length > 0) {
                tagList = customTags; // Preserve custom tags
            } else {
                // Fallback to original tags
                tagList = (songData.tags || song.value?.tags || []).map(t => typeof t === 'object' ? (t.name || t.tag || t) : t).filter(Boolean);
            }

            const payload = { 
                lyric: rawHtmlContent, 
                content: rawHtmlContent,
                songMetadataChanged: {
                    ...parsedMetadata, // Spread to preserve any other metadata overrides
                    title: title,
                    artist: artist,
                    media: mediaList,
                    tags: tagList
                }
            };
            if (currentChord !== 0) {
                payload.reverse_transpose = -currentChord;
            }

            // Persist transpose semitone in backend database
            try {
                await fetch(`/api/playlist/${route.params.id}/song/transpose/${route.params.songId}?semitone=${currentChord}&realtimeStatus=${enabledRealtime.value}`);
            } catch (te) {
                console.error('Error persisting transpose on saveSong:', te);
            }

            await songService.cloneOrSaveSongOnPlaylist(route.params.id, route.params.songId, {realtimeStatus: enabledRealtime.value}, payload, songVersionId);
        } else {
            if (isTextType) {
                await songService.updateNote(route.params.id, route.params.songId, { text: rawHtmlContent });
            }
        }
    } catch (e) {
        console.error('Error in saveSong request:', e);
    } finally {
        // ALWAYS close editor and unblock interaction immediately
        editingSong.value = false;
        appStore.state.disableEvents = false;
    }

    const playlistId = route.params.id;
    const songId = route.params.songId;

    // Offline-first: persiste o editado nas camadas de cache ANTES de recarregar.
    // Só invalidar (como antes) deixava o reload forçado sem nada para ler offline
    // e a tela travava no skeleton. Online o fetch sobrescreve com o fresco.
    try {
        const p = playlistStore.state.playlist;
        if (p && playlistId) {
            const playlistKey = playlistService.PLAYLIST_CACHE_KEY.replace("[id]", playlistId);
            if (window.cacheInstance) window.cacheInstance.setItem(playlistKey, p, playlistService.ttl);
            dbService.setItem(playlistKey, p).catch(() => {});
        }
        const cur = playlistStore.state.currentSong || song.value;
        if (cur && playlistId && songId) {
            const songKey = playlistService.PLAYLIST_SONG_CACHE_KEY
                .replace("[id]", playlistId)
                .replace("[songId]", songId);
            if (window.cacheInstance) window.cacheInstance.setItem(songKey, cur, playlistService.ttl);
            dbService.setItem(songKey, cur).catch(() => {});
        }
    } catch (e) {
        playlistService.clearCache(playlistService.PLAYLIST_CACHE_KEY.replace("[id]", playlistId));
    }

    // Refresh store in background (com timeout: backend pendurado não trava no skeleton)
    playlistStore.state.forceUpdate = true;
    withOnlineTimeout(
        playlistStore.dispatch("load", {...route.params, force: true, realtimeStatus: enabledRealtime.value}),
        'save-reload'
    ).catch((e) => {
        console.warn('Error reloading playlist after save:', e);
        playlistStore.commit('LOADING', false);
    });
}

const saveMusicNotation = (mNotation) => {
    songService.saveMusicNotation(route.params.id, route.params.songId, {notation: mNotation})
    .then(async (response) => {
        let data = await response.json();
        if (response.ok) {
            musicNotation.value = data.notation;
            toastStore.dispatch("addMessage", {message: t('toast.musicNotationSaved'), icon: 'success'});
        } else {
            toastStore.dispatch("addMessage", {message: t('toast.errorSavingMusicNotation'), icon: 'error'});
        }
    })
    .catch((e) => {
        toastStore.dispatch("addMessage", {message: t('toast.errorSavingMusicNotation'), icon: 'error'});
    })
}

const fetchMusicNotation = async () => {
        songService.fetchMusicNotation(route.params.id, route.params.songId)
        .then(async (response) => {
            let data = await response.json();
            if (data.status === 200) {
                nextTick( () => {
                    musicNotation.value = data.notation;
                    musicNotationComponent.value.updateNotation(data.notation);
                })
            } else {
                toastStore.dispatch("addMessage", {message: data.message, icon: 'info'});
                nextTick( () => {
                    musicNotation.value = '';
                    musicNotationComponent.value.updateNotation('');
                })
            }
        })
        .catch((e) => {
            toastStore.dispatch("addMessage", {message: t('toast.errorFetchingMusicNotation'), icon: 'error'});
            musicNotation.value = '';
        })
}

const download = () => {
    // Open download options modal instead of direct download
    downloadOptionsModal.value.open();
}

const performDownload = async (options) => {
    // Build query parameters based on options
    const params = new URLSearchParams();
    
    params.append('chords', options.showChords ? 'true' : 'false');
    params.append('notes', options.showNotes ? 'true' : 'false');
    params.append('songMap', options.showSongMap ? 'true' : 'false');
    params.append('columns', options.columnLayout || 'column-1');
    params.append('orientation', options.orientation || 'portrait');
    
    const queryString = params.toString();
    const url = `/api/playlist/${route.params.id}/download/pdf?${queryString}`;

    // No app nativo o download NÃO abre browser: vai pelo DownloadManager
    // (plugin Downloader) em background, com notificação do sistema.
    if (isNativePlatform()) {
        try {
            if (getPlatform() === 'android') {
                const Downloader = Capacitor.registerPlugin('Downloader');
                const sessionId = localStorage.getItem('triad_session_id') || '';
                const safeName = (playlistStore.state.playlist.name || 'playlist')
                    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-zA-Z0-9-_]+/g, '_')
                    .replace(/_+/g, '_')
                    .slice(0, 80) || 'playlist';
                await Downloader.download({
                    url: resolveAppUrl(url),
                    sessionId,
                    filename: `${safeName}.pdf`,
                    description: t('playlist.downloadingPdf')
                });
                toastStore.dispatch('addMessage', { message: t('playlist.downloadStarted'), icon: 'success' });
            } else {
                await Browser.open({ url: resolveAppUrl(url) });
                toastStore.dispatch('addMessage', { message: t('playlist.downloadBrowser'), icon: 'success' });
            }
        } catch (nativeErr) {
            console.error('Native download error:', nativeErr);
            toastStore.dispatch('addMessage', { message: t('playlist.downloadFailed'), icon: 'error' });
        } finally {
            isDownloading.value = false;
        }
        return;
    }

    try {
        isDownloading.value = true;

        const response = await fetch(url, {
            method: 'GET'
        });

        if (!response.ok) {
            throw new Error(`Download failed with status ${response.status}`);
        }

        const blob = await response.blob();
        const safeName = (playlistStore.state.playlist.name || 'playlist')
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9-_]+/g, '_')
            .replace(/_+/g, '_')
            .slice(0, 80) || 'playlist';
        const filename = `${safeName}.pdf`;
        const blobUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
        console.error('Download error:', error);
        toastStore.dispatch('addMessage', { message: t('playlist.downloadFailed'), icon: 'error' });
    } finally {
        isDownloading.value = false;
    }
}

const updateLyric = (_content) => {
    content.value = _content;
}

playlistStore.watch( () => playlistStore.state.currentSong, (newValue) => {
    if (!newValue) {
        song.value = null;
        return;
    }

    const curPId = playlistStore.state.playlist?.id || playlistStore.state.playlist?._id;
    if (curPId && route.params.id && String(curPId) !== String(route.params.id)) {
        song.value = null;
        return;
    }

    const currentSongIdVal = song.value?.id || song.value?._id || song.value?.data?.id || song.value?.data?._id;
    const newSongIdVal = newValue?.id || newValue?._id || newValue?.data?.id || newValue?.data?._id;
    const isSameSong = currentSongIdVal && newSongIdVal && currentSongIdVal === newSongIdVal;

    if (isSameSong) {
        const songKey = String(newSongIdVal || '');
        // Respostas fora de ordem (ex: fetch do eco AAAA resolvendo após o de BBBB):
        // só adota a mais recente por música; stale é descartada sem tocar na tela.
        const incomingEpoch = newValue._fetchEpoch;
        if (incomingEpoch !== undefined && lastAdoptedFetchEpoch[songKey] !== undefined
            && incomingEpoch < lastAdoptedFetchEpoch[songKey]) {
            console.log(`[PlaylistSongView] Ignoring stale song payload (epoch ${incomingEpoch} < ${lastAdoptedFetchEpoch[songKey]})`);
            return;
        }
        if (incomingEpoch !== undefined) {
            lastAdoptedFetchEpoch[songKey] = incomingEpoch;
        } else {
            delete lastAdoptedFetchEpoch[songKey];
        }
        const prevLocal = song.value;
        const prevChord = prevLocal?.chord;
        const inCooldown = (Date.now() - (localTransposeAt[songKey] || 0) < LOCAL_TRANSPOSE_COOLDOWN_MS)
            || transposePersistFlight.value?.songId === songKey
            || transposePersistQueued?.songId === songKey;
        const incomingChord = (newValue.chord === undefined || newValue.chord === null || newValue.chord === '')
            ? null : Number(newValue.chord);
        song.value = newValue;
        // Resposta/eco stale no cooldown do próprio transpose: não reverte o otimismo.
        // Agenda healing fetch para convergir quando o backend estiver consistente.
        if (inCooldown && incomingChord !== null && Number.isFinite(incomingChord)
            && prevChord !== undefined && Number(prevChord) !== incomingChord) {
            song.value = prevLocal;
            setTimeout(() => scheduleAuthoritativeSongFetch(songKey, { force: true, delay: 1200 }), 0);
            return;
        }
        const hasAuthoritative = (newValue.chord !== undefined && newValue.chord !== null)
            || newValue.modifiedLyric || newValue.data?.content || newValue.data?.lyric;
        if (hasAuthoritative) {
            // Converge para o backend (fonte da verdade): mesmo triple em todos os clientes.
            adoptAuthoritativeTranspose(song.value, newValue);
        } else {
            // Sem dados do backend (offline/draft): preserva estado local contra flash/reset
            if (prevLocal?.modifiedLyric && !newValue.modifiedLyric) {
                newValue.modifiedLyric = prevLocal.modifiedLyric;
            }
            if (prevLocal?.baseLyric && !newValue.baseLyric) {
                newValue.baseLyric = prevLocal.baseLyric;
            }
            if (prevLocal?.chord !== undefined && newValue.chord === undefined) {
                newValue.chord = prevLocal.chord;
            }
        }
        return;
    }

    selectedMedia.value = null
    currentMedia.value = 0
    medias.value = []
    isOpenPlayer.value = false
    songReady.value = false
    song.value = newValue

    // Semeia a base deterministicamente (navegação com tom salvo !== 0 converge igual em todos).
    if (song.value && (song.value.chord !== undefined || song.value.data?.content)) {
        adoptAuthoritativeTranspose(song.value, song.value);
    }

    // Load configuration immediately when song is available
    if (song.value) {
        loadColumnPreference();
        loadZoomValue();
    }
    
    if (song.value && song.value.data){
        processMedias(song.value.data.media)
    }
    setTimeout( () => {
        // Load preferences for the new song (ensure all are loaded after DOM updates)
        loadColumnPreference();
        loadZoomValue();
        loadVisibleChordsPreference();
        loadChordDiagramsPreference();
        changeChordsVisibility(chordsStore.state.visible)
        updateFavoriteMenuProps();

    },100)
}, { immediate: true })

const onColumnChange = () =>{
    if (isMobile(['iPad'])){
        columnView.value = columnValues[0];
        return;
    } else {
        columnView.value = columnValues[(currentColumnIndex + 1) % columnValues.length]
        currentColumnIndex++;
    }
    // Save the new column preference to localStorage
    saveColumnPreference(columnView.value)
}

const currentChordsVisibility = (value) => {
    saveVisibleChordsPreference(value);
    changeChordsVisibility(value);
}

const changeChordsVisibility = (value) => {
    Array.from(document.getElementsByClassName("hightlight-chord"))
    .concat(Array.from(document.getElementsByClassName("chord-container")))
    .concat(Array.from(document.getElementsByClassName("chord-sup")))
    .forEach(element => {
        if (element){
            element.style.display = currentStyle[value]
        }
    })
}

const toggleNotes = (value) => {
    showNotes.value = value;
    toggleNotesElements(value);
}

const toggleNotesElements = (value) => {
    Array.from(document.getElementsByClassName("note"))
    .concat(Array.from(document.querySelectorAll(".draggable-wrapper:has(.user-note)")))
    .forEach(element => {
        if (element){
            if (value){
                element.style.display = 'inline'
            } else {
                element.style.display = 'none'
            }
        }
    })   

    Array.from(document.getElementsByClassName("song-canvas"))
    .forEach(element => {
        if (element){
            element.style.display = value ? '' : 'none'
        }
    })
}

onBeforeMount( () => {
    // Load non-song-specific preferences before loading the page
    // Column and zoom preferences will be loaded after playlist/song loads
    loadVisibleChordsPreference();
    loadChordDiagramsPreference();

    applyPreferences();
    loadPage()
});

const initializePreferences = () => {
    loadColumnPreference()
    loadZoomValue()
    loadVisibleChordsPreference();
    loadChordDiagramsPreference();

    applyPreferences();
}

const applyPreferences = () => {
    changeChordsVisibility(chordsStore.state.visible)
}

const onChangeSong = (state) => {
    if (!state || !state.data) return;
    const targetSongId = state.data._id || state.data.songId || state.data.id || (state.data.song && (state.data.song._id || state.data.song.id));
    const targetPlaylistId = state.data.playlistId || route.params.id || currentId.value;
    if (targetSongId && (String(targetSongId) !== String(route.params.songId) || (targetPlaylistId && String(targetPlaylistId) !== String(route.params.id)))){
        isReactingToBroadcast.value = true;
        // Troca imediata na memória do receptor (0ms)
        if (playlistStore.state.playlist && playlistStore.state.playlist.songs) {
            const target = playlistStore.state.playlist.songs.find(s => 
                (s.data?.id || s.data?._id) === targetSongId || 
                (s.songID || s.song) === targetSongId || 
                s.id === targetSongId
            );
            if (target) {
                playlistStore.commit("UPDATE_SONG", target);
            }
        }
        router.replace({name: "Playlist Songs", params: {id: targetPlaylistId, songId: targetSongId} })
    }
    // Stop scrolling when song changes
    // if (scrollControlComponent.value){
    //     scrollControlComponent.value.stopScrolling()
    // }
    if (favoriteBar.value){
        const menu = favoriteBar.value.getComponentRef('autoscroll')
        if (menu){
            menu.stopScrolling();
        }
    }
}
const updateSong = () => {
    playlistStore.state.forceUpdate = true;
    playlistStore.dispatch("loadSong", route.params);
    toastStore.dispatch("addMessage", {message: t('toast.songUpdated'), icon: 'success'})
    loadVisibleChordsPreference();
    loadChordDiagramsPreference();
    changeChordsVisibility(chordsStore.state.visible)
}
const reloadSong = (options) => {
    playlistStore.state.loading = true;
    playlistStore.state.forceUpdate = true;
    playlistStore.dispatch("loadSong", {...route.params, ...options}).then( () => {
        playlistStore.state.loading = false;
        loadVisibleChordsPreference();
        loadChordDiagramsPreference();
        loadColumnPreference();
        changeChordsVisibility(chordsStore.state.visible)
    })
    
}

let playlistUpdateDeferredTimer = null;
const playlistUpdate = (opts = {}) => {
    // O persist de transpose do Go também emite playlist-update. Recarregar a playlist inteira
    // aqui leria estado pré-commit e revertia o otimismo (4 -> 3). Adia para após o cooling.
    const sid = String(getVisibleSongId());
    const cooling = (Date.now() - (localTransposeAt[sid] || 0) < LOCAL_TRANSPOSE_COOLDOWN_MS)
        || transposePersistFlight.value || transposePersistQueued;
    if (cooling && !opts.force) {
        console.log('[PlaylistSongView] playlistUpdate deferred (transpose cooling)');
        if (playlistUpdateDeferredTimer) clearTimeout(playlistUpdateDeferredTimer);
        playlistUpdateDeferredTimer = setTimeout(() => {
            playlistUpdateDeferredTimer = null;
            playlistUpdate({ force: true });
        }, LOCAL_TRANSPOSE_COOLDOWN_MS + 500);
        return;
    }
    playlistStore.state.forceUpdate = true;
    playlistStore.dispatch("load", {...route.params, realtimeStatus: !appStore.state.disableEvents})
    toastStore.dispatch("addMessage", {message: t('toast.playlistUpdated'), icon: 'success', ttl: 5000})
}
const playlistUpdateOrder = async (update) => {
    if (update){
        if (await dialogStore.dispatch('confirm', "The playlist was sorted. Would like to update?")){
            playlistStore.state.forceUpdate = true;
            playlistStore.dispatch("load", {...route.params, realtimeStatus: enabledRealtime.value});
            toastStore.dispatch("addMessage", {message: t('toast.playlistOrderUpdated'), icon: 'success'})
        }
    }
}
// Timestamps da última mudança aplicada por playlist (Fase 2b: ignora eventos stale fora de ordem).
const lastRealtimeUserTs = {};
const lastPlaylistSnapshotTs = {};
// Dedupe de signals (Go/Ably + transport dual entregam o mesmo conteúdo com mids distintos).
let lastSignalKey = '';
let lastSignalAt = 0;

// Aplica designação de controller (origem Go/Ably ou transport WebRTC). Fonte única para
// não divergir entre os dois caminhos. fromTransport=true respeita guarda de staleness via ts.
const applyRealtimeUserChanged = (data, fromTransport = false) => {
    if (!data || !data.realtime_user_changed) return false;
    const playlistId = data.playlistId || currentId.value || route.params.id;
    if (fromTransport && data.ts) {
        const last = lastRealtimeUserTs[playlistId] || 0;
        if (data.ts < last) {
            console.log('[PlaylistSongView] ignoring stale realtime_user_changed');
            return false;
        }
        lastRealtimeUserTs[playlistId] = data.ts;
    }
    const currentUserId2 = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
    const newControllerId = data.realtime_user_id;
    const isNewController = !!currentUserId2 && !!newControllerId && String(newControllerId) === String(currentUserId2);
    console.log(`[PlaylistSongView] realtime_user_changed: newControllerId=${newControllerId}, isNewController=${isNewController}, enabledRealtime=${enabledRealtime.value}, via=${fromTransport ? 'transport' : 'go'}`);
    playlistStore.commit('PATCH_REALTIME_USER', {
        realtime_user_id: data.realtime_user_id,
        realtime_user: data.realtime_user,
    });
    if (isNewController && !appStore.state.enabledRealtime) {
        console.log('[PlaylistSongView] auto-enabling realtime for new controller');
        appStore.commit('SET_ENABLED_REALTIME', true);
    }
    if (playlistId) {
        const cacheKey = playlistService.PLAYLIST_CACHE_KEY.replace("[id]", playlistId);
        playlistService.clearCache(cacheKey);
        dbService.removeItem(cacheKey).catch(()=>{});
        // Handler do realtime: invalida SW para futuros loads não ressuscitarem realtime_user_id stale.
        // Não dispara load aqui de propósito — o PATCH acima já atualiza badge/controle.
        playlistService.invalidateServiceWorkerCache(playlistId);
    }
    const name = data.realtime_user
        ? (data.realtime_user.full_name || data.realtime_user.username)
        : (isNewController ? 'You' : (playlistStore.state.playlist?.user?.name || 'Owner'));
    toastStore.dispatch("addMessage", {message: t('toast.realtimeControlDelegated', { name }), icon: 'success', ttl: 3000});
    return true;
};

const onEvent = (state) => {
    if (!state) return;
    console.log(`[PlaylistSongView] onEvent received: ${state.event} enabledRealtime=${enabledRealtime.value} sender_client_id=${state.data?.sender_client_id} me=${window.myClientId}`);
    const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
    // Quando o evento traz sender_client_id (publicado pelo frontend), ele identifica o aparelho
    // emissor com precisão. Comparar só o userId descartava eventos do MESMO usuário logado em
    // outro aparelho (ex: app no celular + web/outro app com a mesma conta).
    const hasClientId = !!(state.data && state.data.sender_client_id);
    // update-song is exempt: the Studio app publishes edits with the SAME userId and
    // without sender_client_id, so dropping by sender would freeze the live text while
    // the "updated" toast still shows. The authoritative refetch it triggers is
    // idempotent (server version wins), so re-fetching on our own echo is harmless.
    const isUpdateSongEvent = /^update-song\:\:[0-9a-f-]{24,36}$/.test(state.event || '');
    if (!isUpdateSongEvent && !hasClientId && currentUserId && state.data && state.data.sender === currentUserId) {
        console.log(`[PlaylistSongView] onEvent ignored (sender is current user: ${currentUserId})`);
        return;
    }
    
    if (window.myClientId && state.data && state.data.sender_client_id === window.myClientId) {
        console.log(`[PlaylistSongView] onEvent ignored (sender_client_id matches myClientId: ${window.myClientId})`);
        return;
    }
    // Realtime control delegation must be processed even when enabledRealtime is false,
    // otherwise owner->cto.developer@gmail.com delegation never reaches the new controller
    // and his canControlRealtime stays false. This also auto-enables realtime for him.
    // Designação via transport dual (Fase 2b: palco offline). Go usa o branch playlist-update abaixo.
    if (state.event === 'realtime-user-changed' && state.data && state.data.realtime_user_changed) {
        const pid = state.data.playlistId || currentId.value || route.params.id;
        if (pid && currentId.value && String(pid) !== String(currentId.value)) {
            console.log(`[PlaylistSongView] Ignoring realtime-user-changed for playlist ${pid}`);
            return;
        }
        applyRealtimeUserChanged(state.data, true);
        return;
    }
    if ((/^playlist-update\:\:[0-9a-f-]{24,36}\:\:[0-9a-f-]{24,36}$/.test(state.event)
            || /^playlist-update\:\:[0-9a-f-]{24,36}$/.test(state.event)
            || /^update$/.test(state.event)) && state.data && state.data.realtime_user_changed) {
        applyRealtimeUserChanged(state.data, false);
        return;
    }
    // Snapshot de playlist via transport (Fase 2b: palco offline). Merge preserva a música
    // atual; triple local arbitrado pelo watcher. Silencioso (sem toast).
    if (state.event === 'playlist-update-snapshot' && state.data && state.data.playlist) {
        const snap = state.data.playlist;
        const pid = state.data.playlistId || snap.id || snap._id;
        if (pid && currentId.value && String(pid) !== String(currentId.value)) {
            console.log(`[PlaylistSongView] Ignoring playlist snapshot for ${pid}`);
            return;
        }
        if (state.data.ts && (lastPlaylistSnapshotTs[pid] || 0) > state.data.ts) {
            console.log('[PlaylistSongView] ignoring stale playlist snapshot');
            return;
        }
        lastPlaylistSnapshotTs[pid] = state.data.ts || Date.now();
        playlistStore.commit('APPLY_PLAYLIST_SNAPSHOT', { playlist: snap });
        return;
    }
    if (!enabledRealtime.value){
        console.log(`[PlaylistSongView] onEvent ignored (enabledRealtime false) for event=${state.event}`);
        return;
    }
    if (state.event === "change-song") {
        onChangeSong(state)
        return
    }
    if (state.event === "sync-state") {
        console.log('[PlaylistSongView] sync-state recebido:', state.data);
        if (state.data && (state.data.songId || state.data._id || state.data.id)) {
            onChangeSong(state);
        }
        return;
    }
    if (state.event === "sync-request") {
        console.log('[PlaylistSongView] sync-request recebido:', state.data);
        if (isEffectiveController.value && currentSongId.value && currentId.value) {
            const currentUserId = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
            const payload = {
                _id: currentSongId.value,
                id: currentSongId.value,
                songId: currentSongId.value,
                playlistId: currentId.value,
                sender: currentUserId,
                sender_client_id: window.myClientId || null
            };
            console.log('[PlaylistSongView] respondendo a sync-request com sync-state:', payload);
            realtimeService.publish(`playlist::room::${currentId.value}`, 'sync-state', payload).catch(e => console.warn('[PlaylistSongView] sync-state error:', e));
        }
        return;
    }
    if(/^update-song\:\:[0-9a-f-]{24,36}$/.test(state.event)) {
        // Fluxo autoritativo: busca a música salva no backend e adota (nunca matemática local,
        // que divergia entre clientes com bases diferentes). Ignora se for outra música.
        const eventSongId = state.event.split('::')[1];
        const currentSong = getVisibleSongId();
        if (eventSongId && currentSong && String(eventSongId) !== String(currentSong)) {
            console.log(`[PlaylistSongView] Ignoring update-song for ${eventSongId} — current song is ${currentSong}`);
            return;
        }
        scheduleAuthoritativeSongFetch(eventSongId || currentSong);
        return
    }
    // Transpose via transport dual (Ably + WebRTC): único caminho que funciona em palco
    // offline (sem backend). Aplica placeholder imediato e agenda o fetch autoritativo
    // com debounce — online o fetch reconcilia, offline o placeholder permanece.
    if (state.event === 'transpose' && state.data && (state.data.songId || state.data.song_id)) {
        const tsong = String(state.data.songId || state.data.song_id);
        const currentSong = getVisibleSongId();
        if (tsong !== String(currentSong)) {
            console.log(`[PlaylistSongView] Ignoring transport transpose for song ${tsong} — current song is ${currentSong}`);
            return;
        }
        const semi = Number(state.data.semitone);
        if (!Number.isFinite(semi)) return;
        const sid = tsong;
        const pid = route.params.id || currentId.value;
        if (applyTransposeLocally(semi, Number(song.value?.chord ?? 0))) {
            // Espelha as regras de snap/pino do adopt para o placeholder não divergir:
            // viewer acompanha o editor (offset 0), fixado mantém o efetivo.
            if (!canControlRealtime.value) {
                const pinned = getPinnedTone(sid, pid);
                if (pinned != null) {
                    personalTransposeOffset[sid] = pinned - semi;
                } else {
                    persistPersonalOffset(pid, sid, 0);
                }
            }
            if (sid) lastSharedChord[sid] = semi;
            syncTransposeMenuDisplay();
        }
        scheduleAuthoritativeSongFetch(tsong);
        return;
    }
    if (state.event === 'share-note' && state.data && (state.data.note || state.data.target)) {
        // Nota compartilhada via transport dual (WebRTC incluso). Dedup por id no addUserNotes.
        addUserNotes(state.data.note || state.data.target, true);
        return;
    }
    if(/^playlist-update\:\:[0-9a-f-]{24,36}\:\:[0-9a-f-]{24,36}$/.test(state.event)
        || /^playlist-update\:\:[0-9a-f-]{24,36}$/.test(state.event)
        || /^update$/.test(state.event)) {
        playlistUpdate()
        return
    }
    if(/^playlist-update\:\:[0-9a-f-]{24,36}\:\:order$/.test(state.event)) {
        if (state.data) {
            playlistUpdateOrder(state.data.update)
        }
        return
    }
        if (state.event==="signal") {
            // Dedupe: POST-signal (Go/Ably) + transport dual entregam o mesmo conteúdo.
            try {
                const skey = JSON.stringify({ t: state.data?.type, g: state.data?.target });
                const now = Date.now();
                if (skey === lastSignalKey && now - lastSignalAt < 1500) {
                    console.log('[PlaylistSongView] Ignoring duplicate signal');
                    return;
                }
                lastSignalKey = skey;
                lastSignalAt = now;
            } catch (e) {}
            if (state.data) {
                if (state.data.type==="live-performance") {
                    let targetObj = state.data.target;
                    if (typeof targetObj === 'string') {
                        try {
                            targetObj = JSON.parse(targetObj);
                        } catch(e) {}
                    }
                    if (targetObj && targetObj.action === 'transpose') {
                        // Compat com signals legados: trata como gatilho de fetch autoritativo
                        // (nunca matemática local) para convergir com o backend.
                        const signalSongId = targetObj.songId || targetObj.song_id || getVisibleSongId();
                        const currentSong = getVisibleSongId();
                        if (signalSongId && currentSong && String(signalSongId) !== String(currentSong)) {
                            console.log(`[PlaylistSongView] Ignoring transpose signal for song ${signalSongId} — current song is ${currentSong}`);
                            return;
                        }
                        scheduleAuthoritativeSongFetch(signalSongId || currentSong);
                        return;
                    }
                    const value = typeof state.data.target === 'string' ? /true/i.test(state.data.target) : !!state.data.target;
                    onLivePerformance(value);
                    appStore.state.livePerformanceInControl = !value;
                    return;
                } else {
                    if (state.data.type==="share-note") {
                        addUserNotes(state.data.target, true);
                        return;
                    }
                }
            }
        }
}

onMounted( () => {
    isMounted = true;
    appStore.state.currentView = route.name
    currentId.value = route.params.id
    currentSongId.value = route.params.songId;
    registerEvents();
    wakeLockService.acquire();
    nativeP2PService.setStateProvider(() => {
        return {
            songId: currentSongId.value || route.params.songId,
            playlistId: currentId.value || route.params.id,
            key: chordsStore.state.currentSongKey || null,
            bpm: autoscrollStore.state.bpm || null,
            timestamp: Date.now()
        };
    });
    if (typeof window !== 'undefined') {
        window.addEventListener('resize', updateScreenSize);
    }
})

onUnmounted( () => {
    isMounted = false;
    appStore.commit('SET_FOCUS_MODE', false);
    if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updateScreenSize);
    }
    wakeLockService.release();
    nativeP2PService.setStateProvider(null);
    if (stageSyncRoom) {
        realtimeService.unsubscribe("stage-sync");
        stageSyncRoom = null;
    }
    if (onlineSyncWatchdog) {
        clearTimeout(onlineSyncWatchdog);
        onlineSyncWatchdog = null;
    }
    if (playlistRoom) {
        realtimeService.unsubscribe("playlist::room::"+currentId.value);
        removeChannelFromStorage("playlist::room::"+currentId.value);
    }

    if (songRoom) {
        realtimeService.unsubscribe("playlist::room::"+currentId.value+"::"+currentSongId.value);
        removeChannelFromStorage("playlist::room::"+currentId.value+"::"+currentSongId.value);
    }

    initialState();
    unregisterEvents();
})
const initialState = () => {
    onLivePerformance(false);
    sendSignalLivePerformance(false).catch(() => {});
}

watch(
    () => route.params.id,
    () => {
        songReady.value = false;
        loadPage()
})

watch(
  () => route.params.songId,
  () => {
    loadPage()
    // Load configuration when song ID changes
    nextTick(() => {
        loadColumnPreference();
        loadZoomValue();
        loadInstrumentAndCapoPreference();
    });
  }
)

function onConnection() {
    connected.value=true
}
function onDisconnection() {
    connected.value=false
}

const toggleUpdates = (update) => {
    appStore.commit('SET_ENABLED_REALTIME', update);
}

const presentation = () => {
    initialState();
    router.push({name: "Presentation Controller", params: {id: route.params.id}})
}



const openShareModal = () => {
    shareModalStore.dispatch("open", route.params.id);
};

// Quem gerencia o realtime (owner/editor/admin) abre o share direto na aba de controle
// ao clicar no badge "Control:" — menos cliques para trocar o designado.
const canManageRealtimeControl = computed(() => {
    const userInfo = userStore.state.userInfo;
    const isAdmin = userInfo?.role === 'admin' || userInfo?.isAdmin
        || userInfo?.membershipTier === 'admin_tier' || userInfo?.membershipTier === 'admin_membership';
    return isOwner() || isEditor() || isAdmin;
});

const openRealtimeControl = () => {
    if (!canManageRealtimeControl.value) return;
    const playlistId = route.params.id || currentId.value
        || playlistStore.state.playlist?.id || playlistStore.state.playlist?._id;
    if (!playlistId) return;
    shareModalStore.dispatch("open", { playlistId, tab: 'realtime' });
};

const openSongList = () => {
    isSongListOpen.value = true;
};

const closeSongList = () => {
    isSongListOpen.value = false;
};

const nextMedia = ()  => {
    if (currentMedia.value + 1 < medias.value.length) {
        currentMedia.value++;
        playSong();
    }
}
const previousMedia = () => {
    if (currentMedia.value > 0){
        currentMedia.value--;
        playSong();
    }
}
const openMediaSidePanel = () => {
    mediaSidePanel.value?.open();
}
const playSong = () => {
    openMediaSidePanel();
}
const close = () => {
    selectedMedia.value = null;
    isOpenPlayer.value = !!selectedMedia.value;
    startPlaybackTime.value = 0;
}

const checkAutoPlay = () => {
    if (selectedMedia.value && startPlaybackTime.value > 0) {
        isOpenPlayer.value = true;
    }
}

const playInMainPlayer = ({ song: songToPlay, currentTime }) => {
    const targetSongId = songToPlay.data?._id || songToPlay.song_id || songToPlay.songID || songToPlay.song || songToPlay.id || songToPlay._id;
    const currentId = route.params.songId;
    
    startPlaybackTime.value = currentTime;
    
    if (targetSongId !== currentId) {
        router.push({ name: "Playlist Songs", params: { id: route.params.id, songId: targetSongId } });
    } else {
        selectedMedia.value = medias.value.length > 0 ? medias.value[currentMedia.value] : null;
        isOpenPlayer.value = !!selectedMedia.value;
    }
};

const processMedias = (mediaList) => {
    if (mediaList){
        medias.value = mediaList.map( (m, idx) => {
            return {
                type: m.type || getType(m), 
                name: m.name || "Media "+(idx+1), 
                preview: m.preview || m, 
                images: m.images ? m.images.map(i => typeof(i) == "string"? i: i.url ) : []
            }
        }).filter( (m) => m.preview);
        selectedMedia.value = medias.value.length > 0? medias.value[0] : null
        if (!selectedMedia.value) {
            appEvents.emit('no-media');
        } else {
            appEvents.emit('has-media');
        }
        checkAutoPlay();
    } else {
        lookupMedia()
    }
}
const getSongId = () => {
    return route.params.songId || song.value?.song || song.value?.data?.song || song.value?.data?._id || song.value?.songObjectId || song.value?._id || song.value?.id || '';
}

const lookupMedia = async () => {
    const isSongType = song.value?.type === 'SONG' || !!song.value?.data;
    if (isSongType) {
        await songService.fetchMedia(getSongId()).then( async (response) => {
            const data = await response.json();
            medias.value = data.map( (m, idx) => ({
                type: m.type || 'youtube', name: m.name || song.value.data?.title + " Media #"+ (idx+1), preview: m.preview, images: m.images
            })).filter( (m) => m.preview);
            if (medias.value.length > 0) {
                appEvents.emit('has-media');
            } else {
                appEvents.emit('no-media');
            }
        }).catch( () => {
            medias.value = [];
            if (song.value && song.value.data){
                song.value.data.media = medias.value;
            }
            appEvents.emit('no-media');
        });
        selectedMedia.value = medias.value.length > 0? medias.value[0] : null
        if (!selectedMedia.value) {
            appEvents.emit('no-media');
        } else {
            appEvents.emit('has-media');
        }
        checkAutoPlay();
    } else {
        appEvents.emit('no-media');
    }
}
const getType = (m) => {
    const str = (typeof m === 'string' ? m : (m?.preview || m?.url || '')).toLowerCase();
    if (str.indexOf("youtube") > -1 || str.indexOf("youtu.be") > -1) {
        return "youtube"
    } else {
        if (str.indexOf("p.scdn.co") > -1) {
            return "spotify"
        } else {
            return "unknow";
        }
    }
}

// Adota o triple autoritativo do backend (conteúdo + tom + base derivada) no objeto local.
// A base é derivada deterministicamente do MESMO conteúdo autoritativo em todos os clientes,
// então todos convergem para os mesmos bytes ( Sem isso, cada cliente derivava a base do que
// tinha em tela e os tons/flats divergiam entre usuários).
const adoptAuthoritativeTranspose = (target, backend) => {
    if (!target || !backend) return;
    const rawChord = backend.chord;
    let chord = (rawChord === undefined || rawChord === null || rawChord === '') ? 0 : Number(rawChord);
    if (!Number.isFinite(chord)) chord = 0;
    const authoritativeContent = backend.modifiedLyric || backend.data?.content || backend.data?.lyric || '';
    if (!authoritativeContent) return;
    // Snap do viewer: se o tom compartilhado MUDOU (editor moveu), o viewer acompanha o valor
    // do editor — offset pessoal zera em vez de somar sobre o novo tom (ex: local 2 + editor 0->1 = 1, não 3).
    // Primeira carga mantém o offset persistido; controller nunca usa offset.
    const sid = songIdentityKey(target) || songIdentityKey(backend) || getVisibleSongId();
    const pid = route.params.id || currentId.value;
    const prevShared = sid ? lastSharedChord[sid] : undefined;
    if (sid) lastSharedChord[sid] = chord;
    if (canControlRealtime.value) {
        if (sid && (personalTransposeOffset[sid] || transposePinMap[sid] != null)) {
            persistPersonalOffset(pid, sid, 0);
            setPinnedTone(sid, pid, null);
        }
    } else if (sid) {
        const pinned = getPinnedTone(sid, pid);
        if (pinned != null) {
            // Tom fixado: mantém o efetivo ajustando o offset ao novo compartilhado.
            personalTransposeOffset[sid] = pinned - chord;
        } else if (prevShared !== undefined && prevShared !== chord) {
            persistPersonalOffset(pid, sid, 0);
        }
    }
    const baseLyric = chord === 0 ? authoritativeContent : transposeHtmlChords(authoritativeContent, -chord);
    // Store/song guardam sempre o COMPARTILHADO (fonte da verdade). O tom pessoal do viewer
    // vive só na exibição (viewerContent/viewerEffectiveChord) e nunca contamina estes campos.
    target.chord = chord;
    target.baseLyric = baseLyric;
    target.modifiedLyric = authoritativeContent;
    if (target.data) {
        target.data.content = authoritativeContent;
        target.data.lyric = authoritativeContent;
    }
    content.value = authoritativeContent;
    if (playlistStore.state.currentSong) {
        playlistStore.state.currentSong.chord = chord;
        playlistStore.state.currentSong.modifiedLyric = authoritativeContent;
        playlistStore.state.currentSong.baseLyric = baseLyric;
        if (playlistStore.state.currentSong.data) {
            playlistStore.state.currentSong.data.content = authoritativeContent;
            playlistStore.state.currentSong.data.lyric = authoritativeContent;
        }
    }
    syncTransposeMenuDisplay();
    loadVisibleChordsPreference();
    changeChordsVisibility(chordsStore.state.visible);
};

// Exibição do menu de transpose: tom efetivo (compartilhado + offset pessoal para o viewer).
const syncTransposeMenuDisplay = () => {
    const eff = viewerEffectiveChord.value;
    transposeStore.state.transpose = eff;
    updateTransposePinMirror();
    if (favoriteBar.value) {
        favoriteBar.value.setProps('transpose', { song: song.value });
        const transposeMenu = favoriteBar.value.getComponentRef('transpose');
        if (transposeMenu) transposeMenu.updateSemitone(eff);
    }
};

// Receptor de transpose: busca autoritativa com debounce (coalesce rajadas de +/-).
// Sem realtimeStatus para o Go não re-broadcastar; force:true + no-store para furar caches.
const scheduleAuthoritativeSongFetch = (songId, opts = {}) => {
    const key = String(songId || '');
    if (!key) return;
    // Sou o controller com persist em voo/enfileirado: meu reconcile vem via TRANSPOSE, não via fetch.
    if (transposePersistFlight.value?.songId === key || transposePersistQueued?.songId === key) return;
    // Cooldown pós-transpose local: ecos do próprio persist (sem sender) ou leitura pré-commit
    // do backend reverteriam o otimismo — só o healing forçado fura o cooldown.
    if (!opts.force && (Date.now() - (localTransposeAt[key] || 0) < LOCAL_TRANSPOSE_COOLDOWN_MS)) return;
    if (transposeAuthoritativeTimers[key]) clearTimeout(transposeAuthoritativeTimers[key]);
    transposeAuthoritativeTimers[key] = setTimeout(() => {
        delete transposeAuthoritativeTimers[key];
        if (String(getVisibleSongId()) !== key) return;
        playlistStore.state.forceUpdate = true;
        playlistStore.dispatch("loadSong", { id: route.params.id || currentId.value, songId: key, force: true });
    }, opts.delay ?? 350);
};

// Aplica transpose matematicamente sobre a música em tela (otimismo do controller
// ou placeholder offline do receptor). Não persiste nem notifica. Retorna false se
// delta 0 ou sem música. O reconcile autoritativo vem depois via adopt/fetch.
const applyTransposeLocally = (targetSemitone, prevSemitone) => {
    if (!Number.isFinite(targetSemitone) || !song.value) return false;
    if (!Number.isFinite(prevSemitone)) prevSemitone = 0;
    if (targetSemitone - prevSemitone === 0) return false;

    transposeStore.state.transpose = targetSemitone;
    song.value.chord = targetSemitone;

    // Initialize baseLyric if not present yet
    if (!song.value.baseLyric) {
        if (prevSemitone === 0) {
            song.value.baseLyric = song.value.modifiedLyric || song.value.data?.content || song.value.data?.lyric || '';
        } else {
            // If song was loaded already transposed, reverse-transpose to obtain the base (semitone 0) lyric
            const currentContent = song.value.modifiedLyric || song.value.data?.content || song.value.data?.lyric || '';
            song.value.baseLyric = transposeHtmlChords(currentContent, -prevSemitone);
        }
    }

    const transposedHtml = targetSemitone === 0
        ? song.value.baseLyric
        : transposeHtmlChords(song.value.baseLyric, targetSemitone);

    song.value.modifiedLyric = transposedHtml;
    if (song.value.data) {
        song.value.data.content = transposedHtml;
        song.value.data.lyric = transposedHtml;
    }
    content.value = transposedHtml;

    // Also keep store currentSong in sync
    if (playlistStore.state.currentSong) {
        playlistStore.state.currentSong.chord = targetSemitone;
        playlistStore.state.currentSong.modifiedLyric = transposedHtml;
        playlistStore.state.currentSong.baseLyric = song.value.baseLyric;
        if (playlistStore.state.currentSong.data) {
            playlistStore.state.currentSong.data.content = transposedHtml;
            playlistStore.state.currentSong.data.lyric = transposedHtml;
        }
    }
    updateFavoriteMenuProps();
    loadVisibleChordsPreference();
    changeChordsVisibility(chordsStore.state.visible);
    return true;
};

const onTranspose = (value, isRemoteEvent = false) => {
    // Eventos legados via HTTP/signal: receptores buscam o autoritativo (ver scheduleAuthoritativeSongFetch).
    if (isRemoteEvent) return;
    const isController = canControlRealtime.value;
    // O menu trabalha no espaço EFETIVO (compartilhado + offset pessoal do viewer).
    const effectiveBase = isController
        ? Number(song.value?.chord ?? transposeStore.state.transpose ?? 0)
        : Number(transposeStore.state.transpose ?? song.value?.chord ?? 0);
    let targetEffective;
    if (typeof value === 'number') {
        targetEffective = Number(value);
    } else if (value && typeof value.semitone !== 'undefined') {
        targetEffective = Number(value.semitone);
    } else if (value && typeof value.current !== 'undefined') {
        // Eventos locais do menu (TriadTranspose/menus) com delta relativo.
        targetEffective = (Number.isFinite(effectiveBase) ? effectiveBase : 0) + Number(value.current);
    } else {
        return;
    }
    if (!Number.isFinite(targetEffective)) return;

    // Quem NÃO controla faz transpose PESSOAL: grava o offset (efetivo - compartilhado),
    // persiste por usuário/música e exibe via viewerContent — sem mutar o compartilhado,
    // sem persist no backend, sem busy (resposta instantânea, sem roundtrip).
    if (!isController) {
        const sharedChord = Number(song.value?.chord ?? 0);
        const shared = Number.isFinite(sharedChord) ? sharedChord : 0;
        const sid = songIdentityKey(song.value) || getVisibleSongId();
        const pid = route.params.id || currentId.value;
        if (!sid) return;
        // Com tom fixado, o +/- move o próprio pino (continua ignorando o editor).
        if (getPinnedTone(sid, pid) != null) {
            if (targetEffective === viewerEffectiveChord.value) return;
            setPinnedTone(sid, pid, targetEffective);
            personalTransposeOffset[sid] = targetEffective - shared;
            syncTransposeMenuDisplay();
            return;
        }
        if (personalTransposeOffset[sid] === undefined) {
            personalTransposeOffset[sid] = loadPersonalOffset(pid, sid);
        }
        const newOffset = targetEffective - shared;
        if (newOffset === (personalTransposeOffset[sid] || 0)) return;
        persistPersonalOffset(pid, sid, newOffset);
        syncTransposeMenuDisplay();
        return;
    }

    const targetSemitone = targetEffective;
    let prevSemitone = Number(song.value?.chord ?? transposeStore.state.transpose ?? 0);
    if (!Number.isFinite(prevSemitone)) prevSemitone = 0;
    let delta = targetSemitone - prevSemitone;

    if (delta === 0) {
        return;
    }

    // 1. Instant 0ms optimistic local transposition in UI
    if (!applyTransposeLocally(targetSemitone, prevSemitone)) {
        return;
    }

    // 2. Persist encadeado (somente o controller chega aqui): um voo por vez, sempre com
    // o target mais recente (latest wins). Garante ordem no backend (writes absolutos) e elimina
    // o vai-e-volta de respostas fora de ordem. O reconcile vem via TRANSPOSE -> watcher -> adopt.
    // O Go salva + broadcast update-song; o signal frontend dá o caminho rápido para quem perdeu o broadcast.
    const transposeSongId = getVisibleSongId();
    localTransposeAt[String(transposeSongId)] = Date.now();
    transposePersistQueued = { songId: transposeSongId, target: targetSemitone };
    pumpTransposePersist();
}

// Bomba do persist encadeado de transpose (single-flight por cliente).
const pumpTransposePersist = () => {
    if (transposePersistFlight.value || !transposePersistQueued) return;
    const job = transposePersistQueued;
    transposePersistQueued = null;
    transposePersistFlight.value = job;
    isTransposing.value = true;
    transposeStore.state.busy = true;
    const playlistId = route.params.id || currentId.value;
    stampLocalSongEdit(job.songId);
    playlistStore.dispatch("transpose", {
            id: playlistId,
            songId: job.songId,
            current: Number(song.value?.chord ?? job.target),
            semitone: job.target,
            realtimeStatus: enabledRealtime.value
    }).catch(e => {
        console.warn('Transpose background save error:', e);
    }).finally(() => {
        localTransposeAt[String(job.songId)] = Date.now();
        transposePersistFlight.value = null;
        if (transposePersistQueued) {
            pumpTransposePersist();
        } else {
            isTransposing.value = false;
            transposeStore.state.busy = false;
        }
        // Notificação redundante ao broadcast do Go, via transport dual (Ably + WebRTC):
        // cobre palco offline (sem backend) e quem perdeu o broadcast. Receptores aplicam
        // placeholder imediato + fetch autoritativo com debounce (sem divergência).
        // Dispara apenas quando o usuário está com Realtime Updates habilitado.
        if (canControlRealtime.value && enabledRealtime.value) {
            sendSignalLivePerformance({ action: 'transpose', semitone: job.target, songId: job.songId });
            try {
                const cUid = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
                realtimeService.publish(`playlist::room::${playlistId}`, 'transpose', {
                    songId: job.songId,
                    semitone: job.target,
                    sender: cUid,
                    sender_client_id: window.myClientId || null
                }).catch(() => {});
            } catch (e) {}
        }
    });
}

const addUserNotes = (note = null, shared = false) => {
    toggleNotes(true);
    if (userNotesRef.value) {
        if (note) {
            userNotesRef.value.addNotes(note, shared);
        } else {
            userNotesRef.value.addNotes({
                id: Date.now(),
                note: t('userNotes.clickToEdit'),
                createdAt: new Date(),
                x: 0,
                y: 0,
                width: 150,
                height: 100
            });
        }
    }
}





const openArtistInfoModal = () => {
    artistInfoModal.value?.open();
};

const closeArtistInfoModal = () => {
    artistInfoModal.value?.close();
};

const isSongPublic = computed(() => {
    const s = song.value;
    if (!s) return false;
    return !!(s.isPublic || s.is_public || s.data?.isPublic || s.data?.is_public || s.userData?.isPublic || s.userData?.is_public);
});

const reportModal = ref(null);
const openReportModal = () => {
    const s = song.value?.data || song.value?.userData || song.value;
    const id = s?.song_id || s?.songID || s?.song || s?._id || s?.id || currentSongId.value;
    const songTitle = song.value?.userData?.title || song.value?.data?.title || s?.title || s?.name || '';
    reportModal.value?.open(id, songTitle);
};



const toggleSongMap = () => {
    songMapVisible.value = !songMapVisible.value
}

const duplicatePlaylist = async () => {
    if (await dialogStore.dispatch('confirm', "Are you sure you want to duplicate this playlist?")){
        const response = await fetch("/api/playlist/"+route.params.id+"/duplicate")
        const data = await response.json();
        if (response.ok){
            toastStore.dispatch("addMessage", {message: t('toast.playlistDuplicated', { name: data.name }), icon: 'success'});
            router.push({path: "/playlist/"+data._id});
        } else {
            if (data && (data.error === 'LIMIT_REACHED' || data.message === 'LIMIT_REACHED')) {
                // The fetch interceptor handles displaying the detailed limit reached toast
                return;
            }
            toastStore.dispatch("addMessage", {message: t('toast.errorDuplicatingPlaylist'), icon: 'error'});
        }
    }
}

const zoomIn = () => {
    zoomValue.value = Number(Math.min(zoomValue.value + 0.1, 2).toFixed(1));
    zoomStore.state.zoom = zoomValue.value;
    saveZoomValue(zoomValue.value);
    appEvents.emit('currentZoom', zoomValue.value);
}

const zoomOut = () => {
    zoomValue.value = Number(Math.max(zoomValue.value - 0.1, 0.5).toFixed(1));
    zoomStore.state.zoom = zoomValue.value;
    saveZoomValue(zoomValue.value);
    appEvents.emit('currentZoom', zoomValue.value);
}

const onDrawingEnabled = (enabled) => { 
    if (drawingEnabled.value !== enabled) {
        drawingEnabled.value = enabled;
        appEvents.emit('currentDrawingState', enabled);
    }
}

const getRoomId = () => {
    const playlistId = route.params.id
    const songId = getSongId()
    return `playlist-${playlistId}-song-${songId}`
}

const getUserId = () => {
    const userInfo = userStore.state.userInfo
    if (userInfo && userInfo.id) {
        return `user-${userInfo.id}`
    }
    return `anonymous-${Math.random().toString(36).substr(2, 9)}`
}

const storeKey = computed(() => {
    const uId = userStore.state.userInfo?.id || 'anon';
    return 'notes-user-' + uId + '-playlist-' + route.params.id + '-song-' + getSongId()
})

const onError = (error) => {
    toastStore.dispatch('addMessage', {
        message: error,
        icon: 'error',
        ttl: 3000
    });
}

watch( () =>  playlistStore.state.loading, (isLoading) => {
    if (isLoading) {
        songReady.value = false;
    }
    // When loading completes, ensure configuration is loaded and applied
    if (!isLoading && song.value) {
        nextTick( () => {
            loadColumnPreference();
            initializePreferences();
            applyPreferences();
        });
    }
})

// Watch for userInfo changes and reload configuration when user becomes available
watch( () => userStore.state.userInfo, (newUserInfo) => {
    if (newUserInfo && song.value) {
        // User just logged in or user info became available, reload configuration
        nextTick( () => {
            loadColumnPreference();
            loadZoomValue();
        });
    }
}, { immediate: true })
const MaxCounterLivePerformance = 4000;
const intervalLivePerformance = ref(null);
const preparingToStart = ref(false);

const startLivePerformance = (value) => {
    onLivePerformance(value);
    appStore.state.livePerformanceInControl = true;
    if(value) {
        sendSignalLivePerformance(true);
    } else {
        sendSignalLivePerformance(false);
    }
}

const onLivePerformance = (value) => {
    if (value) {
        preparingToStart.value = true;
        intervalLivePerformance.value = setInterval( () => {
            
            if (counterLivePerformance.value * 1000 >= MaxCounterLivePerformance) {
                appStore.state.livePerformance = value;
                counterLivePerformance.value = 1;
                clearInterval(intervalLivePerformance.value)
                preparingToStart.value = false;
                return;
            }
            counterLivePerformance.value++;
        }, 1000);
    } else {
        clearInterval(intervalLivePerformance.value)
        preparingToStart.value = false;
        appStore.state.livePerformance = value;
        
        counterLivePerformance.value = 1;
        intervalLivePerformance.value = null;
        
    }
};

const stopLivePerformance = () => {
    if (timeCounter.value) {
        timeCounter.value.stopTimer();
        timeCounter.value.resetTimer();
    }
    appStore.state.livePerformance = false;
    appStore.state.livePerformanceInControl = false;
    preparingToStart.value = false;
    counterLivePerformance.value = 1;
    intervalLivePerformance.value = null;
}

const sendSignalLivePerformance = async (value) => {
    if (!canControlRealtime.value || !enabledRealtime.value) {
        return;
    }

    if (currentId.value || route.params.id){
        const playlistId = currentId.value || route.params.id;
        const payload = {
            type: 'live-performance',
            target: value
        };
        // Via transport dual (Fase 2b: palco offline). Receptores usam o MESMO branch
        // de signal abaixo — nunca matemática nova, então não há divergência.
        try {
            const cUid = userStore.state.userInfo?.id || userStore.state.userInfo?._id;
            realtimeService.publish(`playlist::room::${playlistId}`, 'signal', {
                ...payload,
                sender: cUid,
                sender_client_id: window.myClientId || null
            }).catch(() => {});
        } catch (e) {}
        try {
            const response = await fetch("/api/playlist/"+playlistId+"/signal", {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            if (!response.ok){
                toastStore.dispatch("addMessage", {message: t('toast.errorSendingSignal'), icon: 'error'});
            }
        } catch (e) {
            // Offline (palco): o transport acima já notificou; sem toast de erro.
            console.warn('[PlaylistSongView] signal POST failed (offline?), transport fallback used');
        }
    }
}

const toggleMusicNotation = async () => {
    musicNotationVisible.value = !musicNotationVisible.value;
    if (musicNotationVisible.value) {
        await fetchMusicNotation();
    }
}

const updateFavorite = (menu) => {
    if (favoriteBar.value) {
        favoriteBar.value.toggleMenu(menu.menu.id);
    }
}

// Sincroniza o metrônomo global (montado em App.vue) com a música atual.
// Bloco posicionado após currentSongBpm/saveSongBpm (evita TDZ no setup).

const currentSongBpm = computed(() => {
    const songData = song.value?.data || song.value || {};
    // Priority: playlist songMetadataChanged bpm > song.bpm
    const metadataChanged = song.value?.songMetadataChanged || song.value?.song_metadata_changed;
    if (metadataChanged) {
        let parsed = {};
        if (typeof metadataChanged === 'string') {
            try { parsed = JSON.parse(metadataChanged); } catch (e) {}
        } else {
            parsed = metadataChanged;
        }
        if (parsed.bpm) return Number(parsed.bpm);
    }
    if (songData.bpm) return Number(songData.bpm);

    // Fallback: per-song stored user preference
    const playlistId = route.params.id || currentId.value;
    const songId = getVisibleSongId();
    if (playlistId && songId) {
        const savedBpm = userConfigService.getCurrentUserSongConfigProperty(playlistId, songId, 'metronomeBpm', null);
        if (savedBpm) return Number(savedBpm);
    }

    return 120;
});

const saveSongBpm = async (newBpm) => {
    const playlistId = route.params.id || currentId.value;
    const songId = getVisibleSongId();
    if (playlistId && songId) {
        userConfigService.setCurrentUserSongConfigProperty(playlistId, songId, 'metronomeBpm', newBpm);
    }
    if (!song.value || !route.params.id || !route.params.songId) return;
    try {
        const metadataChanged = song.value.songMetadataChanged || song.value.song_metadata_changed;
        let parsedMetadata = {};
        if (typeof metadataChanged === 'string') {
            try { parsedMetadata = JSON.parse(metadataChanged); } catch (e) {}
        } else if (metadataChanged) {
            parsedMetadata = metadataChanged;
        }

        parsedMetadata.bpm = newBpm;
        const payload = {
            songMetadataChanged: parsedMetadata
        };

        const songVersionId = song.value?.song_version_id || song.value?.songVersionId || null;
        await songService.cloneOrSaveSongOnPlaylist(
            route.params.id,
            route.params.songId,
            { realtimeStatus: enabledRealtime.value },
            payload,
            songVersionId
        );

        if (song.value) {
            song.value.songMetadataChanged = parsedMetadata;
            if (song.value.data) {
                song.value.data.bpm = newBpm;
            }
        }

        toastStore.dispatch('addMessage', {
            message: t('toast.bpmSaved', { bpm: newBpm }),
            icon: 'success',
            ttl: 3000
        });
    } catch (err) {
        console.error('Error saving song BPM:', err);
        toastStore.dispatch('addMessage', {
            message: t('toast.errorSavingBpm'),
            icon: 'error',
            ttl: 3000
        });
    }
};

// Sincroniza o metrônomo global (montado em App.vue) com a música atual.
// Posicionado após currentSongBpm/saveSongBpm (evita TDZ no setup).
const syncMetronomeContext = () => {
    appEvents.emit('metronome:context', {
        bpm: currentSongBpm.value,
        canSave: isEditor() || isOwner(),
    });
};

const handleSaveBpm = (newBpm) => {
    saveSongBpm(newBpm);
};

onMounted(() => {
    appEvents.on('metronome:saveBpm', handleSaveBpm);
    syncMetronomeContext();
});

onUnmounted(() => {
    appEvents.off('metronome:saveBpm', handleSaveBpm);
});

watch([currentSongBpm, song], () => {
    syncMetronomeContext();
});

const onFavoriteMenuEvent = (event, ...args) => {
    switch (event) {
        case 'onTranspose':
            //onTranspose(args[0]);
            break;
        case 'toggleTuner':
            tunerStore.toggle();
            break;
        default:
            break;
    }
}

const handleRefresh = async () => {
    if (isRefreshing.value || editingSong.value || drawingEnabled.value || appStore.state.livePerformance) return;
    isRefreshing.value = true;
    const startTime = Date.now();
    try {
        const pId = route.params.id || currentId.value;
        const sId = route.params.songId || currentSongId.value;

        // Invalida cache local da playlist e música atual para recarregar da nuvem
        if (pId) {
            playlistService.clearCache(playlistService.PLAYLIST_CACHE_KEY.replace('[id]', pId));
            if (sId) {
                playlistService.clearCache(
                    playlistService.PLAYLIST_SONG_CACHE_KEY.replace('[id]', pId).replace('[songId]', sId)
                );
            }
        }

        // Força recarregamento da playlist e da música no store
        playlistStore.state.forceUpdate = true;
        await playlistStore.dispatch("load", {
            id: pId,
            songId: sId,
            force: true,
            realtimeStatus: enabledRealtime.value
        });

        if (sId) {
            await playlistStore.dispatch("loadSong", {
                id: pId,
                songId: sId,
                force: true,
                realtimeStatus: enabledRealtime.value
            });
        }

        // Restaura preferências locais após recarregar
        loadVisibleChordsPreference();
        loadChordDiagramsPreference();
        loadColumnPreference();
        changeChordsVisibility(chordsStore.state.visible);

        const elapsed = Date.now() - startTime;
        if (elapsed < 450) {
            await new Promise(resolve => setTimeout(resolve, 450 - elapsed));
        }

        toastStore.dispatch("addMessage", {
            message: t('playlistSong.repertoireRefreshed') || 'Repertório atualizado com sucesso',
            icon: 'success'
        });
    } catch (err) {
        toastStore.dispatch("addMessage", {
            message: t('toast.errorUpdatingSong') || 'Erro ao atualizar repertório',
            icon: 'error'
        });
    } finally {
        isRefreshing.value = false;
    }
};
</script>